// src/indicators/engine.js

// Pure formulas (no time alignment) live in utils
import { altmanZScore, evEbitdaYield } from "../utils/financialIndicators";
import { forwardEstimateRows, ntmEstimateRows } from "../utils/analystForecasts";

export { INDICATOR_OPTIONS } from "./indicatorOptions";
import {
  DEFAULT_Z_SCORE_YEARS,
  getZScoreYearsForIndicator,
  isLegacyZScore5yKey,
  isZScoreConfigurable,
  normalizeZScoreIndicatorKey,
  yearsToLookbackSec,
} from "./zScoreConfig";
import {
  isClassicNovelCombo,
  normalizeComboMembers,
  shouldFlipComboMember,
} from "./comboConfig";

/**
 * Map a logical indicator id to the time series stored in `buildSeriesCache` output.
 */
export function getSeriesForIndicatorKey(cache, key, zScoreYears = DEFAULT_Z_SCORE_YEARS, extra = {}) {
  if (!cache || !key) return [];
  if (isClassicNovelCombo(key)) {
    return computeClassicNovelCombo(
      cache,
      extra.comboMembers,
      extra.zScoreYearsMap || {}
    );
  }
  if (isZScoreConfigurable(key)) {
    return computeZScoreSeriesForIndicator(cache, key, zScoreYears);
  }
  switch (key) {
    case "ev_ebitda":
      return cache.evYield || [];
    case "div_yield":
      return cache.divYield || [];
    case "pb_ratio":
      return (cache.pbNormalized?.length ? cache.pbNormalized : cache.pb) || [];
    case "pe_ratio":
      return (cache.peNormalized?.length ? cache.peNormalized : cache.pe) || [];
    case "poi_ratio":
      return (cache.poiNormalized?.length ? cache.poiNormalized : cache.poi) || [];
    case "ps_ratio":
      return (cache.psNormalized?.length ? cache.psNormalized : cache.ps) || [];
    case "cf_price":
      return cache.cfPriceNormalized || [];
    case "peg_ratio":
      return cache.pegQ || [];
    case "syo":
      return cache.syo || [];
    case "rmo":
      return cache.rmo || [];
    case "tamo":
      return cache.tamo || [];
    case "nimo":
      return cache.nimo || [];
    case "vmz":
      return cache.vmz || [];
    case "srm":
      return cache.srm || [];
    case "oey":
      return cache.oey || [];
    case "vmo":
      return cache.vmo || [];
    case "mrvz":
      return cache.mrvz || [];
    case "vddm":
      return cache.vddm || [];
    case "nupl":
      return cache.nupl || [];
    case "realized_price_252":
      return cache.realizedPrice252 || [];
    case "realized_price_504":
      return cache.realizedPrice504 || [];
    case "realized_price_5y":
      return cache.realizedPrice5y || [];
    case "risk":
      return cache.risk || [];
    case "pe_peratio":
      return cache.pePERatio || [];
    case "forward_pe":
      return cache.forwardPe || [];
    case "ttm_pe":
      return cache.ttmPe || [];
    case "revenue_cagr":
      return cache.revenueCagr || [];
    case "eps_cagr":
      return cache.epsCagr || [];
    case "rev_val":
      return (cache.revVal?.series || []).map((p) => ({ time: p.time, value: p.value }));
    case "eps_val":
      return (cache.epsVal?.series || []).map((p) => ({ time: p.time, value: p.value }));
    case "fgpz":
      return cache.fgpz || [];
    case "gag":
      return cache.gag || [];
    case "ed":
      return cache.ed || [];
    default:
      return [];
  }
}

export const PROB_COLS = ["Chance Loss", "0–5%", "5–15%", ">15%"];
export const MIN_N_FOR_CHANCES = 6;
export const RSI_FORWARD_DAYS = 63; // ~1 quarter on trading days
export const REALIZED_PRICE_5Y_WINDOW = 5 * 252; // ~5 trading years
export const REALIZED_PRICE_WINDOWS = {
  realized_price_252: 252,
  realized_price_504: 504,
  realized_price_5y: REALIZED_PRICE_5Y_WINDOW,
};
export const REALIZED_PRICE_INDICATORS = Object.keys(REALIZED_PRICE_WINDOWS);
export const VOLUME_BAR_INDICATORS = ["mrvz", "vddm", "nupl", "risk", ...REALIZED_PRICE_INDICATORS];
export function isRealizedPriceIndicator(key) {
  return key in REALIZED_PRICE_WINDOWS;
}

/* =========================================================================
   Small utils used across builders
   ========================================================================= */
export const clamp = (x, lo, hi) => Math.max(lo, Math.min(hi, x));

/** Display formatting for indicator values (charts, tables, labels). */
export function formatIndicatorValue(value, { asPercent = false } = {}) {
  if (!Number.isFinite(value)) return "—";
  const s = value.toFixed(2);
  return asPercent ? `${s}%` : s;
}

export function stdev(arr) {
  if (!arr || arr.length < 2) return 0;
  const m = arr.reduce((s, x) => s + x, 0) / arr.length;
  return Math.sqrt(arr.reduce((s, x) => s + (x - m) ** 2, 0) / (arr.length - 1));
}

export function alignDateToInterval(dateString, interval) {
  const d = new Date(dateString);
  if (interval === "weekly") {
    const day = d.getDay();
    d.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
  } else if (interval === "monthly") {
    d.setDate(1);
  }
  return d.toISOString().split("T")[0];
}

export function getFundDate(e) {
  // Unified "information date" for fundamentals
  return new Date(e.fillingDate || e.acceptedDate || e.date);
}


export function toEpoch(ymd) {
  const [Y, M, D] = ymd.split("-").map(Number);
  return Date.UTC(Y, M - 1, D) / 1000;
}

/** Normalize a date string to a canonical quarter key "YYYY-Qn" for alignment between income and cashflow. */
export function dateToQuarterKey(dateString) {
  const d = new Date(dateString);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const q = Math.floor(m / 3) + 1;
  return `${y}-Q${q}`;
}

export function findNearestPriceValue(time, pricePoints) {
  if (!Array.isArray(pricePoints) || !pricePoints.length) return null;
  let lo = 0, hi = pricePoints.length - 1;
  if (time <= pricePoints[0].time) return pricePoints[0].value;
  if (time >= pricePoints[hi].time) return pricePoints[hi].value;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const t = pricePoints[mid].time;
    if (t === time) return pricePoints[mid].value;
    if (t < time) lo = mid + 1; else hi = mid - 1;
  }
  const before = pricePoints[hi], after = pricePoints[lo];
  return (time - before.time) <= (after.time - time) ? before.value : after.value;
}

export function nearestPoint(series, t, maxGapSec = 60 * 60 * 24 * 45) {
  if (!series?.length) return null;
  let lo = 0, hi = series.length - 1;
  if (t <= series[0].time) return (series[0].time - t <= maxGapSec) ? series[0] : null;
  if (t >= series[hi].time) return (t - series[hi].time <= maxGapSec) ? series[hi] : null;
  while (lo <= hi) {
    const m = (lo + hi) >> 1;
    const tm = series[m].time;
    if (tm === t) return series[m];
    (tm < t) ? (lo = m + 1) : (hi = m - 1);
  }
  const before = series[hi], after = series[lo];
  const db = Math.abs(t - before.time), da = Math.abs(after.time - t);
  const d = Math.min(db, da);
  if (d > maxGapSec) return null;
  return db <= da ? before : after;
}
export function priceAtOrAfter(t, px, maxGapSec = null) {
  if (!px?.length) return null;
  let lo = 0, hi = px.length - 1;
  if (t <= px[0].time) return maxGapSec && (px[0].time - t) > maxGapSec ? null : px[0];
  if (t >  px[hi].time) return null;

  // lower_bound for first time >= t
  while (lo <= hi) {
    const m = (lo + hi) >> 1;
    if (px[m].time < t) lo = m + 1; else hi = m - 1;
  }
  const cand = px[lo]; // first >= t
  if (!cand) return null;
  if (maxGapSec && (cand.time - t) > maxGapSec) return null;
  return cand; // { time, value }
}

export function priceAtOrBefore(t, px, maxGapSec = null) {
  if (!px?.length) return null;
  let lo = 0, hi = px.length - 1;
  if (t >= px[hi].time) return maxGapSec && (t - px[hi].time) > maxGapSec ? null : px[hi];
  if (t <  px[0].time) return null;

  // upper_bound for last time <= t
  while (lo <= hi) {
    const m = (lo + hi) >> 1;
    if (px[m].time <= t) lo = m + 1; else hi = m - 1;
  }
  const cand = px[hi]; // last <= t
  if (!cand) return null;
  if (maxGapSec && (t - cand.time) > maxGapSec) return null;
  return cand; // { time, value }
}

export function rollingBands(points, window, maxSigma = 5) {
  if (!points || points.length < window) return { mean: [], sd: [], bands: {} };
  const mean = [], sd = [], bands = {};
  for (let k = 1; k <= maxSigma; k++) bands[k] = [];
  for (let i = 0; i < points.length; i++) {
    const seg = points.slice(Math.max(0, i - window + 1), i + 1).map(p => p.value);
    const mu = seg.reduce((s, x) => s + x, 0) / seg.length;
    const s = stdev(seg);
    mean.push({ time: points[i].time, value: mu });
    sd.push({ time: points[i].time, value: s });
    for (let k = 1; k <= maxSigma; k++) {
      bands[k].push({ time: points[i].time, upper: mu + k * s, lower: mu - k * s });
    }
  }
  return { mean, sd, bands };
}

export function buildDivergence(metricPts, pricePts) {
  const out = [];
  const safePos = x => x != null && isFinite(x) && x > 0;
  for (let i = 1; i < metricPts.length; i++) {
    const a = metricPts[i - 1], b = metricPts[i];
    const pA = findNearestPriceValue(a.time, pricePts);
    const pB = findNearestPriceValue(b.time, pricePts);
    if (!safePos(a.value) || !safePos(b.value) || !safePos(pA) || !safePos(pB)) continue;
    const mRet = Math.log(b.value / a.value);
    const pRet = Math.log(pB / pA);
    out.push({ time: b.time, value: pRet - mRet });
  }
  return out;
}

export function computeFundDivRSI(divPts, N = 4) {
  if (divPts.length < N + 1) return [];
  let gains = 0, losses = 0;
  for (let i = 1; i <= N; i++) {
    const d = divPts[i].value - divPts[i - 1].value;
    if (d >= 0) gains += d; else losses += -d;
  }
  let avgG = gains / N, avgL = losses / N;
  const out = [];
  out.push({ time: divPts[N].time, value: (avgG + avgL === 0) ? 50 : (100 * (avgG / (avgG + avgL))) });
  for (let i = N + 1; i < divPts.length; i++) {
    const d = divPts[i].value - divPts[i - 1].value;
    const g = Math.max(d, 0), l = Math.max(-d, 0);
    avgG = (avgG * (N - 1) + g) / N;
    avgL = (avgL * (N - 1) + l) / N;
    out.push({ time: divPts[i].time, value: (avgG + avgL === 0) ? 50 : (100 * (avgG / (avgG + avgL))) });
  }
  return out;
}

function barsToIntervalPoints(bars, interval, lagDays, valueSel) {
  if (!Array.isArray(bars) || !bars.length) return [];
  const byKey = new Map();
  for (const b of bars) {
    if (!b?.date) continue;
    const d = new Date(b.date);
    if (lagDays) d.setDate(d.getDate() + lagDays);
    const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
    const t = toEpoch(key);
    const v = valueSel(b);
    if (!Number.isFinite(v)) continue;
    const prev = byKey.get(t);
    // average inside bucket
    byKey.set(t, prev == null ? v : (prev + v) / 2);
  }
  return Array.from(byKey.entries())
    .map(([time, value]) => ({ time, value }))
    .sort((a, b) => a.time - b.time);
}

// Realized price proxy: rolling volume-weighted average cost over 252 trading days
function rollingVWAC(bars, window = 252) {
  const out = [];
  let sumPV = 0, sumV = 0;
  const Q = [];
  for (const b of bars) {
    if (!Number.isFinite(b?.close) || !Number.isFinite(b?.volume)) { out.push(null); continue; }
    Q.push({ pv: b.close * b.volume, v: b.volume });
    sumPV += b.close * b.volume; sumV += b.volume;
    if (Q.length > window) { const x = Q.shift(); sumPV -= x.pv; sumV -= x.v; }
    out.push(sumV > 0 ? sumPV / sumV : null);
  }
  return out;
}

function rollingSum(vals, window) {
  const out = [];
  let s = 0;
  const Q = [];
  for (const x of vals) {
    const v = Number.isFinite(x) ? x : 0;
    Q.push(v); s += v;
    if (Q.length > window) s -= Q.shift();
    out.push(Q.length === window ? s : null);
  }
  return out;
}

function rollingZScore(vals, window = 252) {
  const out = [];
  const Q = [];
  let sum = 0, sumsq = 0;
  for (const x of vals) {
    const v = Number.isFinite(x) ? x : 0;
    Q.push(v); sum += v; sumsq += v*v;
    if (Q.length > window) {
      const y = Q.shift(); sum -= y; sumsq -= y*y;
    }
    if (Q.length < window) { out.push(null); continue; }
    const n = Q.length;
    const mean = sum / n;
    const var_ = Math.max(sumsq / n - mean * mean, 0);
    const sd = Math.sqrt(var_);
    out.push(sd > 0 ? (v - mean) / sd : 0);
  }
  return out;
}


// Z-score helper
function zScoreSeries(values, lookback = 252) {
  const out = new Array(values.length).fill(null);
  for (let i = 0; i < values.length; i++) {
    const start = Math.max(0, i - lookback + 1);
    const seg = values.slice(start, i + 1).filter(Number.isFinite);
    if (seg.length < Math.min(30, lookback / 4)) continue;
    const mu = seg.reduce((s, x) => s + x, 0) / seg.length;
    const sd = stdev(seg);
    if (!sd) continue;
    out[i] = (values[i] - mu) / sd;
  }
  return out;
}

/* =========================================================================
   Series builders (ported from fundamentals.js)
   ========================================================================= */
// --- helpers for PEG (self-computed) ---

// Next-12-month (NTM) EPS from quarterly earnings estimates.
// For each earnings print i, sum epsEstimated of i..i+3.
// Output [{time, value}] aligned to the earnings date.
function buildNTMEPS(earnings, interval, lagDays) {
  const src = Array.isArray(earnings) ? earnings.filter(e => e?.date) : [];
  const out = [];

  for (let i = 0; i < src.length; i++) {
    const win = src.slice(i, i + 4);
    if (win.length < 4) break;

    const ests = win.map(e => e.epsEstimated).filter(v => Number.isFinite(v));
    if (ests.length < 4) continue;

    const sum = ests.reduce((s, v) => s + v, 0);

    const d = getFundDate(src[i]);               // <<< release date
    d.setDate(d.getDate() + (lagDays || 0));
    const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
    out.push({ time: toEpoch(key), value: sum });
  }

  return out.sort((a, b) => a.time - b.time);
}


// Forward P/E per price bar using nearest NTM EPS step series at/before each bar (PEG path; uses buildNTMEPS).
function forwardPEFromNtmStepSeries(px, ntmEpsSeries) {
  if (!px?.length || !ntmEpsSeries?.length) return [];
  const out = [];
  let k = 0;
  for (const bar of px) {
    while (k + 1 < ntmEpsSeries.length && ntmEpsSeries[k + 1].time <= bar.time) k++;
    const eps = ntmEpsSeries[k]?.value;
    if (!(eps > 0)) continue;
    const pe = bar.value / eps;
    if (Number.isFinite(pe) && pe > 0) out.push({ time: bar.time, value: pe });
  }
  return out;
}

// TTM CAGR over lookbackYears from a [{time, value}] TTM step series (decimal, not %).
function computeTTMCAGR(ttmSeries, years = 3) {
  if (!ttmSeries?.length) return [];
  const out = [];
  const sec = years * 365 * 24 * 3600;
  let j = 0;
  for (let i = 0; i < ttmSeries.length; i++) {
    const t1 = ttmSeries[i].time;
    const v1 = ttmSeries[i].value;
    if (!(v1 > 0)) continue;
    while (j < i && (t1 - ttmSeries[j].time) > sec) j++;
    // pick the nearest point at or before t1 - sec
    let k = j;
    while (k + 1 < i && (t1 - ttmSeries[k + 1].time) >= sec) k++;
    const prev = ttmSeries[k];
    if (!prev || !(prev.value > 0)) continue;
    const yrs = (t1 - prev.time) / (365 * 24 * 3600);
    if (!(yrs > 0.5)) continue;
    const g = Math.pow(v1 / prev.value, 1 / yrs) - 1; // decimal CAGR
    if (Number.isFinite(g)) out.push({ time: t1, value: g });
  }
  return out;
}

// PEG (self-computed): PEG = Forward PE / Growth%
// We clamp min growth to 3% to avoid divide-by-peanuts.
function computePEGCustom(px, earnings, income, interval, lagDays, growthYears = 3) {
  const ntm = buildNTMEPS(earnings, interval, lagDays);
  const fpe = forwardPEFromNtmStepSeries(px, ntm);         // forward P/E by bar
  const epsTTM = buildTTM(income, "eps", lagDays, interval);
  const gSeries = computeTTMCAGR(epsTTM, growthYears);     // decimal CAGR

  const out = [];
  let k = 0;
  for (const p of fpe) {
    while (k + 1 < gSeries.length && gSeries[k + 1].time <= p.time) k++;
    const g = gSeries[k]?.value;                            // decimal
    if (!(Number.isFinite(g))) continue;
    const gPct = Math.max(0.03, g) * 100;                   // clamp to >=3%
    const peg = p.value / gPct;
    if (Number.isFinite(peg) && peg > 0) out.push({ time: p.time, value: peg });
  }
  return out;
}

// Percentile version of PEG over 5y window (0..100)
function computePEGQuantile(pegSeries, lookbackYears = 5) {
  if (!pegSeries?.length) return [];
  const FIVE_Y = lookbackYears * 365 * 24 * 3600;
  const pts = rollingPercentile(pegSeries, FIVE_Y, p => p.value)
    .filter(p => Number.isFinite(p?.value));
  return pts;
}

// Rolling percentile rank over a lookback in seconds
function rollingPercentile(points, lookbackSec, accessor = p => p.value) {
  if (!Array.isArray(points) || !points.length) return [];
  const out = [];
  let i0 = 0; // window start index

  // For performance we won't maintain a fancy tree; we'll just slice+sort per step.
  for (let i = 0; i < points.length; i++) {
    const t = points[i].time;
    while (i0 < i && (t - points[i0].time) > lookbackSec) i0++;
    const window = points.slice(i0, i + 1).map(accessor).filter(v => Number.isFinite(v));
    if (window.length < 8) continue;
    const sorted = window.slice().sort((a,b)=>a-b);
    const v = accessor(points[i]);
    // rank position (nearest-rank method)
    let lo = 0, hi = sorted.length - 1;
    if (v <= sorted[0])  { out.push({ time: t, value: 0 });   continue; }
    if (v >= sorted[hi]) { out.push({ time: t, value: 100 }); continue; }
    while (lo <= hi) {
      const m = (lo + hi) >> 1;
      (sorted[m] < v) ? (lo = m + 1) : (hi = m - 1);
    }
    const pct = Math.round((lo / (sorted.length - 1)) * 100);
    out.push({ time: t, value: pct });
  }
  return out;
}

// Build a BVPS staircase from metrics; fallback by backing out from P/B on metric dates
function buildBVPSSeries(metrics, px) {
  if (!Array.isArray(metrics) || !metrics.length) return [];
  const bvpsPoints = [];
  const byTimePrice = new Map((px || []).map(p => [p.time, p.value]));

    const toTime = (row) => {
    const d = getFundDate(row);
    const key = d.toISOString().split("T")[0];
    const [Y, M, D] = key.split("-").map(Number);
    return Date.UTC(Y, M - 1, D) / 1000;
  };

  for (const m of metrics) {
    if (!m) continue;
    const t = toTime(m);     // <<< release-based timestamp
    let bvps = Number.isFinite(m.bookValuePerShare) ? m.bookValuePerShare : null;

    if (bvps == null) {
      const pb = m.priceToBookRatio ?? m.priceBookValueRatio ?? null;
      if (Number.isFinite(pb) && pb > 0) {
        // nearest price at or before t
        let best = null;
        for (let i = (px||[]).length - 1; i >= 0; i--) {
          if (px[i].time <= t) { best = px[i].value; break; }
        }
        if (Number.isFinite(best) && best > 0) bvps = best / pb;
      }
    }
    if (Number.isFinite(bvps) && bvps > 0) bvpsPoints.push({ time: t, value: bvps });
  }
  // sort & dedupe by time (latest wins)
  const seen = new Set();
  return bvpsPoints
    .sort((a,b)=>a.time-b.time)
    .filter(p => !seen.has(p.time) && seen.add(p.time));
}

// Daily/interval P/B using last-known BVPS carried forward
export function computePBDaily(metrics, px) {
  if (!Array.isArray(px) || !px.length) return [];
  const bvps = buildBVPSSeries(metrics, px);
  if (!bvps.length) return [];

  const out = [];
  let k = 0;
  for (const bar of px) {
    while (k+1 < bvps.length && bvps[k+1].time <= bar.time) k++;
    const b = bvps[k];
    if (!b || !Number.isFinite(b.value) || b.value <= 0) continue;         // skip this bar
    const pb = bar.value / b.value;
    if (!Number.isFinite(pb)) continue;                                     // skip junk
    out.push({ time: bar.time, value: pb });
  }
  return out;
}

// Percentile version (rolling window)
export function computePBQuantile(metrics, px, lookbackYears = 5) {
  const pbDaily = computePBDaily(metrics, px);
  if (!pbDaily.length) return [];
  const LOOKBACK_SEC = lookbackYears * 365 * 24 * 3600;
  return rollingPercentile(pbDaily, LOOKBACK_SEC, p => p.value)
    .filter(p => Number.isFinite(p?.value));
}

// P/B normalized (z-score vs 2-year average)
export function computePBNormalized(metrics, px, lookbackYears = 2) {
  const pbDaily = computePBDaily(metrics, px);
  if (!pbDaily.length) return [];
  const LOOKBACK_SEC = lookbackYears * 365 * 24 * 3600;
  return rollingZScoreTimeSeries(pbDaily, LOOKBACK_SEC, p => p.value)
    .filter(p => Number.isFinite(p?.value));
}

// Daily/interval P/E using last-known TTM EPS carried forward
function carryForwardSeriesToPx(stepSeries, px) {
  if (!stepSeries?.length || !px?.length) return [];
  const out = [];
  let k = 0;
  for (const bar of px) {
    while (k + 1 < stepSeries.length && stepSeries[k + 1].time <= bar.time) k++;
    const pt = stepSeries[k];
    if (!pt || !Number.isFinite(pt.value)) continue;
    out.push({ time: bar.time, value: pt.value });
  }
  return out;
}

/** TTM CAGR (% per year) from income statement field; aligned to each price bar. */
function computeFundamentalCAGR(income, px, field, interval, lagDays, years = 2) {
  if (!Array.isArray(income) || !income.length || !px?.length) return [];
  const ttm = buildTTM(income, field, lagDays, interval);
  const steps = computeTTMCAGR(ttm, years).map((p) => ({
    time: p.time,
    value: p.value * 100,
  }));
  return carryForwardSeriesToPx(steps, px);
}

export function computeRevenueCAGR(income, px, interval, lagDays, years = 2) {
  return computeFundamentalCAGR(income, px, "revenue", interval, lagDays, years);
}

export function computeEPSCAGR(income, px, interval, lagDays, years = 2) {
  return computeFundamentalCAGR(income, px, "eps", interval, lagDays, years);
}

/** Sort earnings rows to epoch times on the chart interval (shared by forward estimate helpers). */
function sortedEarningsOnInterval(earnings, interval, lagDays) {
  return [...(earnings || [])]
    .filter((e) => e?.date)
    .map((e) => {
      const fundDate = getFundDate(e);
      const d = new Date(fundDate);
      d.setDate(d.getDate() + (lagDays || 0));
      const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
      return { ...e, time: toEpoch(key), dateObj: fundDate };
    })
    .sort((a, b) => a.time - b.time);
}

/**
 * Implied 1y growth from current TTM to NTM (sum of next four quarterly estimates), in %.
 * Uses the same estimate sourcing as Forward P/E (analyst next4 when recent, else earnings calendar).
 */
function computeForwardEstTTMCAGR(earnings, income, px, interval, lagDays, analystForecasts, field) {
  if (!Array.isArray(income) || !income.length || !px?.length) return [];
  const ttm = buildTTM(income, field, lagDays, interval);
  if (!ttm.length) return [];
  const sortedEarnings = sortedEarningsOnInterval(earnings, interval, lagDays);
  if (!sortedEarnings.length) return [];

  const nextFour =
    field === "revenue"
      ? (t) => nextFourQtrRevenueEstimatesOrNull(sortedEarnings, t, analystForecasts)
      : (t) => nextFourQtrEpsEstimatesOrNull(sortedEarnings, t, analystForecasts);

  const out = [];
  let ttmK = 0;
  for (const bar of px) {
    while (ttmK + 1 < ttm.length && ttm[ttmK + 1].time <= bar.time) ttmK++;
    const ttmVal = ttm[ttmK]?.value;
    if (!(ttmVal > 0)) continue;
    const four = nextFour(bar.time);
    if (!four) continue;
    const ntm = four.reduce((s, v) => s + v, 0);
    if (!(ntm > 0)) continue;
    const g = (ntm / ttmVal - 1) * 100;
    if (Number.isFinite(g)) out.push({ time: bar.time, value: g });
  }
  return out.sort((a, b) => a.time - b.time);
}

export function computeRevenueForwardCAGR(earnings, income, px, interval, lagDays, analystForecasts = null) {
  return computeForwardEstTTMCAGR(earnings, income, px, interval, lagDays, analystForecasts, "revenue");
}

export function computeEPSForwardCAGR(earnings, income, px, interval, lagDays, analystForecasts = null) {
  return computeForwardEstTTMCAGR(earnings, income, px, interval, lagDays, analystForecasts, "eps");
}

export function computePEDaily(income, px) {
  if (!Array.isArray(px) || !px.length) return [];
  const epsTTM = buildTTM(income, "eps", 0, "daily")
    .filter(p => Number.isFinite(p?.value) && p.value > 0);
  if (!epsTTM.length) return [];

  const out = [];
  let k = 0;
  for (const bar of px) {
    while (k + 1 < epsTTM.length && epsTTM[k + 1].time <= bar.time) k++;
    const e = epsTTM[k];
    if (!e || !Number.isFinite(e.value) || e.value <= 0) continue;
    const pe = bar.value / e.value;
    if (!Number.isFinite(pe) || pe <= 0) continue;
    out.push({ time: bar.time, value: pe });
  }
  return out;
}

// P/E normalized (z-score vs 2-year average)
export function computePENormalized(income, px, lookbackYears = 2) {
  const peDaily = computePEDaily(income, px);
  if (!peDaily.length) return [];
  const LOOKBACK_SEC = lookbackYears * 365 * 24 * 3600;
  return rollingZScoreTimeSeries(peDaily, LOOKBACK_SEC, p => p.value)
    .filter(p => Number.isFinite(p?.value));
}

// Build Sales Per Share (SPS) series using TTM revenue and shares outstanding
function buildSPSSeries(income, px) {
  if (!Array.isArray(income) || !income.length) return [];
  const spsPoints = [];
  const byTimePrice = new Map((px || []).map(p => [p.time, p.value]));

  const toTime = (row) => {
    const d = getFundDate(row);
    const key = d.toISOString().split("T")[0];
    const [Y, M, D] = key.split("-").map(Number);
    return Date.UTC(Y, M - 1, D) / 1000;
  };

  const sortedIncome = forwardFillIncomeField(income, "revenue");
  let lastShares = null;

  // Build TTM revenue series
  for (let i = 3; i < sortedIncome.length; i++) {
    let ttmRevenue = 0;
    let revOk = true;
    for (let k = i - 3; k <= i; k++) {
      const rev = sortedIncome[k].revenue;
      if (rev == null || !Number.isFinite(rev)) {
        revOk = false;
        break;
      }
      ttmRevenue += rev;
    }
    if (!revOk) continue;

    const shRaw = sortedIncome[i].weightedAverageShsOutDil ??
                   sortedIncome[i].weightedAverageShsOut;
    const shares = (shRaw != null && Number.isFinite(shRaw) && shRaw > 0)
      ? shRaw
      : lastShares;
    if (shRaw != null && Number.isFinite(shRaw) && shRaw > 0) lastShares = shRaw;

    if (Number.isFinite(ttmRevenue) && ttmRevenue > 0 && 
        Number.isFinite(shares) && shares > 0) {
      const t = toTime(sortedIncome[i]);
      const sps = ttmRevenue / shares;
      if (Number.isFinite(sps) && sps > 0) {
        spsPoints.push({ time: t, value: sps });
      }
    }
  }

  // Sort & dedupe by time (latest wins)
  const seen = new Set();
  return spsPoints
    .sort((a, b) => a.time - b.time)
    .filter(p => !seen.has(p.time) && seen.add(p.time));
}

// TTM operating income per share (aligned to income statement dates)
function buildOIPSSeries(income, px) {
  if (!Array.isArray(income) || !income.length) return [];
  const oipsPoints = [];
  const sortedIncome = forwardFillIncomeField(income, "operatingIncome");
  let lastShares = null;

  const toTime = (row) => {
    const d = getFundDate(row);
    const key = d.toISOString().split("T")[0];
    const [Y, M, D] = key.split("-").map(Number);
    return Date.UTC(Y, M - 1, D) / 1000;
  };

  for (let i = 3; i < sortedIncome.length; i++) {
    let ttmOI = 0;
    let oiOk = true;
    for (let k = i - 3; k <= i; k++) {
      const oi = sortedIncome[k].operatingIncome;
      if (oi == null || !Number.isFinite(oi)) {
        oiOk = false;
        break;
      }
      ttmOI += oi;
    }
    if (!oiOk) continue;

    const shRaw = sortedIncome[i].weightedAverageShsOutDil ??
                   sortedIncome[i].weightedAverageShsOut;
    const shares = (shRaw != null && Number.isFinite(shRaw) && shRaw > 0)
      ? shRaw
      : lastShares;
    if (shRaw != null && Number.isFinite(shRaw) && shRaw > 0) lastShares = shRaw;

    if (Number.isFinite(ttmOI) && ttmOI > 0 &&
        Number.isFinite(shares) && shares > 0) {
      const t = toTime(sortedIncome[i]);
      const oips = ttmOI / shares;
      if (Number.isFinite(oips) && oips > 0) {
        oipsPoints.push({ time: t, value: oips });
      }
    }
  }

  const seen = new Set();
  return oipsPoints
    .sort((a, b) => a.time - b.time)
    .filter(p => !seen.has(p.time) && seen.add(p.time));
}

/** Daily price ÷ TTM operating income per share (last-known OIPS carried forward). */
export function computePOIDaily(income, px) {
  if (!Array.isArray(px) || !px.length) return [];
  const oips = buildOIPSSeries(income, px);
  if (!oips.length) return [];

  const out = [];
  let k = 0;
  for (const bar of px) {
    while (k + 1 < oips.length && oips[k + 1].time <= bar.time) k++;
    const o = oips[k];
    if (!o || !Number.isFinite(o.value) || o.value <= 0) continue;
    const poi = bar.value / o.value;
    if (!Number.isFinite(poi) || poi <= 0) continue;
    out.push({ time: bar.time, value: poi });
  }
  return out;
}

// Daily/interval P/S using last-known SPS carried forward
export function computePSDaily(income, px) {
  if (!Array.isArray(px) || !px.length) return [];
  const sps = buildSPSSeries(income, px);
  if (!sps.length) return [];

  const out = [];
  let k = 0;
  for (const bar of px) {
    while (k + 1 < sps.length && sps[k + 1].time <= bar.time) k++;
    const s = sps[k];
    if (!s || !Number.isFinite(s.value) || s.value <= 0) continue;  // skip this bar
    const ps = bar.value / s.value;
    if (!Number.isFinite(ps)) continue;                              // skip junk
    out.push({ time: bar.time, value: ps });
  }
  return out;
}

// Rolling z-score normalization for time series (mean and std dev over lookback window)
function rollingZScoreTimeSeries(points, lookbackSec, accessor = p => p.value) {
  if (!Array.isArray(points) || !points.length) return [];
  const out = [];
  let i0 = 0; // window start index

  for (let i = 0; i < points.length; i++) {
    const t = points[i].time;
    while (i0 < i && (t - points[i0].time) > lookbackSec) i0++;
    const window = points.slice(i0, i + 1).map(accessor).filter(v => Number.isFinite(v));
    if (window.length < 8) continue; // Need at least 8 points for meaningful stats
    
    // Calculate mean
    const mean = window.reduce((sum, v) => sum + v, 0) / window.length;
    
    // Calculate standard deviation
    const variance = window.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / (window.length - 1);
    const stdDev = Math.sqrt(variance);
    
    const v = accessor(points[i]);
    if (!Number.isFinite(v)) continue;
    
    // Z-score: (value - mean) / stdDev
    // If stdDev is 0 or very small, return 0 (no deviation)
    const zScore = stdDev > 1e-10 ? (v - mean) / stdDev : 0;
    
    out.push({ time: t, value: zScore });
  }
  return out;
}

/** Rolling Z-score for configurable lookback (calendar years, decimals allowed). */
export function computeZScoreSeriesForIndicator(cache, indicatorKey, years = DEFAULT_Z_SCORE_YEARS) {
  if (!cache) return [];
  const base = normalizeZScoreIndicatorKey(indicatorKey);
  const sec = yearsToLookbackSec(years);
  const z = (pts) =>
    rollingZScoreTimeSeries(pts || [], sec, (p) => p.value).filter((p) => Number.isFinite(p?.value));
  switch (base) {
    case "ev_ebitda_z":
      return z(cache.evYieldRaw || cache.evYield);
    case "div_yield_z":
      return z(cache.divYield);
    case "pb_ratio":
      return z(cache.pb);
    case "pe_ratio":
      return z(cache.pe);
    case "poi_ratio":
      return z(cache.poi);
    case "ps_ratio":
      return z(cache.ps);
    case "cf_price":
      return z(cache.cfPrice || cache.cfPriceDaily);
    default:
      return [];
  }
}

/**
 * Equal-weight average of Classic / Novel Fundamental Z-scores.
 * Yield-style members are sign-flipped so low = cheap across all inputs.
 */
export function computeClassicNovelCombo(cache, members, zScoreYearsMap = {}) {
  if (!cache) return [];
  const keys = normalizeComboMembers(members);
  if (!keys.length) return [];

  const maps = keys.map((key) => {
    const years = getZScoreYearsForIndicator(key, zScoreYearsMap);
    const series = computeZScoreSeriesForIndicator(cache, key, years);
    const flip = shouldFlipComboMember(key) ? -1 : 1;
    const m = new Map();
    for (const p of series || []) {
      if (!p || !Number.isFinite(p.time) || !Number.isFinite(p.value)) continue;
      m.set(p.time, p.value * flip);
    }
    return m;
  });

  const times = new Set();
  for (const m of maps) {
    for (const t of m.keys()) times.add(t);
  }

  const out = [];
  for (const t of [...times].sort((a, b) => a - b)) {
    let sum = 0;
    let n = 0;
    for (const m of maps) {
      if (!m.has(t)) continue;
      sum += m.get(t);
      n += 1;
    }
    if (n > 0) out.push({ time: t, value: sum / n });
  }
  return out;
}

// Normalized P/S ratio (z-score compared to 2-year average)
export function computePSNormalized(income, px, lookbackYears = 2) {
  const psDaily = computePSDaily(income, px);
  if (!psDaily.length) return [];
  const FIVE_Y = lookbackYears * 365 * 24 * 3600;
  return rollingZScoreTimeSeries(psDaily, FIVE_Y, p => p.value)
    .filter(p => Number.isFinite(p?.value));
}

// Daily P/CF (price to cash flow): market cap / TTM cash flow
// Uses latest cash flow (TTM) and daily price for market cap
function computePCFDaily(cashflow, income, px) {
  if (!Array.isArray(px) || !px.length || !cashflow?.length) return [];
  const fcfTTM = buildTTM(cashflow, "freeCashFlow", 0, "quarter");
  if (!fcfTTM.length) return [];

  const sharesQ = (income || [])
    .map(e => {
      const d = new Date(e.fillingDate || e.acceptedDate || e.date);
      const key = alignDateToInterval(d.toISOString().split("T")[0], "quarter");
      const sh = e.weightedAverageShsOutDil ?? e.weightedAverageShsOut ?? null;
      return (sh != null && isFinite(sh)) ? { time: toEpoch(key), value: sh } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.time - b.time);

  const out = [];
  let k = 0;
  let shK = 0;
  for (const bar of px) {
    if (bar.time < fcfTTM[0].time) continue;  // No TTM data before first release
    while (k + 1 < fcfTTM.length && fcfTTM[k + 1].time <= bar.time) k++;
    const f = fcfTTM[k];
    if (!f || !Number.isFinite(f.value) || f.value <= 0) continue;
    if (!sharesQ.length || bar.time < sharesQ[0].time) continue;
    while (shK + 1 < sharesQ.length && sharesQ[shK + 1].time <= bar.time) shK++;
    const sh = sharesQ[shK]?.value;
    const mcap = (bar.value && sh) ? bar.value * sh : null;
    if (!mcap || mcap <= 0) continue;
    const ratioVal = mcap / f.value;  // P/CF (price to cash flow)
    if (Number.isFinite(ratioVal) && ratioVal > 0) out.push({ time: bar.time, value: ratioVal });
  }
  return out;
}

// P/CF ratio: Z-score vs 2-year rolling mean and std (same as P/B and P/S)
export function computeCFYieldNormalized(cashflow, income, px, lookbackYears = 2) {
  const pcfDaily = computePCFDaily(cashflow, income, px);
  if (!pcfDaily.length) return [];
  const LOOKBACK_SEC = lookbackYears * 365 * 24 * 3600;
  return rollingZScoreTimeSeries(pcfDaily, LOOKBACK_SEC, p => p.value)
    .filter(p => Number.isFinite(p?.value));
}

function shiftAndMap(arr, valueSelector, lagDays, interval) {
  return (arr || [])
    .map(e => {
      const d = new Date(e.fillingDate || e.acceptedDate || e.date);
      d.setDate(d.getDate() + lagDays);
      const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
      const val = valueSelector(e);
      return val != null && isFinite(val) ? { time: toEpoch(key), value: val } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.time - b.time);
}

/**
 * Sort by filing date and carry forward the last known numeric value for each listed field
 * so a missing quarter does not zero out TTM sums or spike ratios.
 */
export function forwardFillFundRows(arr, fields) {
  if (!fields?.length) return [...(arr || [])];
  const lasts = Object.fromEntries(fields.map((f) => [f, null]));
  const sorted = [...(arr || [])].sort((a, b) => getFundDate(a) - getFundDate(b));
  return sorted.map((e) => {
    const o = { ...e };
    for (const f of fields) {
      let v = o[f];
      if (v != null && Number.isFinite(v)) lasts[f] = v;
      else if (lasts[f] != null) o[f] = lasts[f];
    }
    return o;
  });
}

export function forwardFillIncomeField(arr, field) {
  return forwardFillFundRows(arr, [field]);
}

export function buildTTM(arr, field, lagDays, interval) {
  const src = forwardFillIncomeField(arr, field);
  const out = [];

  for (let i = 3; i < src.length; i++) {
    let sum = 0;
    let ok = true;
    for (let k = i - 3; k <= i; k++) {
      const v = src[k][field];
      if (v == null || !Number.isFinite(v)) {
        ok = false;
        break;
      }
      sum += v;
    }
    if (!ok) continue;

    const d = getFundDate(src[i]);              // <<< release date
    d.setDate(d.getDate() + (lagDays || 0));
    const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
    out.push({ time: toEpoch(key), value: sum });
  }
  return out;
}


export function computePBSeries(metrics, interval, lagDays) {
  return shiftAndMap(metrics, e => e.priceToBookRatio ?? e.priceBookValueRatio ?? null, lagDays, interval);
}

export function computePEGSeries(metrics, interval, lagDays) {
  return shiftAndMap(metrics, e => e.priceEarningsToGrowthRatio, lagDays, interval);
}

/**
 * EV/EBITDA yield = EBITDA / EV. When price + income + balance sheet exist, EV is repriced on
 * every price bar (MCap = price × latest diluted shares; EV = MCap + debt − cash) with TTM EBITDA
 * and debt/cash stepped at each filing; otherwise falls back to quarterly metrics from the API.
 */
export function computeEVYieldSeries(metrics, interval, lagDays, px = null, income = null, balanceSheet = null) {
  if (Array.isArray(px) && px.length && Array.isArray(income) && income.length && Array.isArray(balanceSheet) && balanceSheet.length) {
    const sharesQ = (income || [])
      .map((e) => {
        const d = new Date(e.fillingDate || e.acceptedDate || e.date);
        d.setDate(d.getDate() + (lagDays || 0));
        const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
        const sh = e.weightedAverageShsOutDil ?? e.weightedAverageShsOut ?? null;
        return sh != null && isFinite(sh) ? { time: toEpoch(key), value: sh } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.time - b.time);

    const debtCashQ = (balanceSheet || [])
      .map((e) => {
        const d = new Date(e.fillingDate || e.acceptedDate || e.date);
        d.setDate(d.getDate() + (lagDays || 0));
        const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
        const t = toEpoch(key);
        const debt =
          e.totalDebt ??
          e.totalLiabilities ??
          (e.longTermDebt ? e.longTermDebt + (e.shortTermDebt || 0) : null) ??
          null;
        const cash =
          e.cashAndCashEquivalents ??
          e.cashAndShortTermInvestments ??
          e.totalCash ??
          e.cash ??
          null;
        return debt != null || cash != null ? { time: t, debt: debt ?? 0, cash: cash ?? 0 } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.time - b.time);

    const ebitdaTTM = buildTTM(income, "ebitda", lagDays, interval);

    if (sharesQ.length && ebitdaTTM.length) {
      const getLatestQ = (series, t) => {
        let best = null;
        for (let i = series.length - 1; i >= 0; i--) {
          if (series[i].time <= t) {
            best = series[i];
            break;
          }
        }
        return best;
      };

      const out = [];
      for (const pricePt of px) {
        if (!pricePt || !Number.isFinite(pricePt.time) || !Number.isFinite(pricePt.value)) continue;
        const sharesPt = getLatestQ(sharesQ, pricePt.time);
        const debtCashPt = getLatestQ(debtCashQ, pricePt.time);
        const ebitdaPt = getLatestQ(ebitdaTTM, pricePt.time);
        if (!sharesPt || !debtCashPt || !ebitdaPt) continue;

        const price = pricePt.value;
        const shares = sharesPt.value;
        const debt = debtCashPt.debt;
        const cash = debtCashPt.cash;
        const ebitda = ebitdaPt.value;

        if (!(price > 0) || !(shares > 0) || !(ebitda > 0)) continue;

        const marketCap = price * shares;
        const ev = marketCap + debt - cash;
        if (!(ev > 0)) continue;

        const y = ebitda / ev;
        if (Number.isFinite(y) && y > 0) out.push({ time: pricePt.time, value: y });
      }

      if (out.length >= 8) return out;
    }
  }

  return shiftAndMap(metrics, (m) => evEbitdaYield(m), lagDays, interval);
}

export function computeDividendYield(cashflow, income, px, interval, lagDays) {
  if (!cashflow?.length || !px?.length) return [];

  const sharesQ = shiftAndMap(
    income,
    e => e.weightedAverageShsOutDil ?? e.weightedAverageShsOut ?? null,
    lagDays,
    interval
  );

  /** TTM dividends per share at each cash-flow filing (numerator); price reprices on every bar below. */
  const dpsSnaps = [];
  const cfSorted = [...cashflow].sort(
    (a, b) => getFundDate(a) - getFundDate(b)    // <<< release sort
  );

  for (let i = 3; i < cfSorted.length; i++) {
    const last4 = cfSorted.slice(i - 3, i + 1);
    const ttmDiv = Math.abs(
      last4.reduce((s, e) => s + (e.dividendsPaid || 0), 0)
    );

    const d = getFundDate(cfSorted[i]);         // <<< release date
    d.setDate(d.getDate() + (lagDays || 0));
    const key  = alignDateToInterval(d.toISOString().split("T")[0], interval);
    const time = toEpoch(key);

    let best = sharesQ[0];
    let bestDiff = best ? Math.abs(time - best.time) : Infinity;
    for (const s of sharesQ || []) {
      const diff = Math.abs(time - s.time);
      if (diff < bestDiff) { best = s; bestDiff = diff; }
    }
    const shares = best?.value || null;
    if (!shares || shares <= 0 || !Number.isFinite(ttmDiv)) continue;

    const dps = ttmDiv / shares;
    if (Number.isFinite(dps) && dps >= 0) dpsSnaps.push({ time, dps });
  }

  if (!dpsSnaps.length) return [];

  dpsSnaps.sort((a, b) => a.time - b.time);
  const priceBars = [...px].filter((p) => p && Number.isFinite(p.time) && Number.isFinite(p.value) && p.value > 0)
    .sort((a, b) => a.time - b.time);

  const out = [];
  let s = -1;
  for (const bar of priceBars) {
    while (s + 1 < dpsSnaps.length && dpsSnaps[s + 1].time <= bar.time) s++;
    if (s < 0) continue;
    const dps = dpsSnaps[s].dps;
    if (!Number.isFinite(dps)) continue;
    out.push({ time: bar.time, value: (dps / bar.value) * 100 });
  }

  return out;
}


export function computeAltmanZ({ balanceSheet, income, px, interval }) {
  if (!balanceSheet?.length || !income?.length) return [];
  const bsByDate = {}; balanceSheet.forEach(b => { bsByDate[b.date] = b; });
  const incByDate = {}; income.forEach(i => { incByDate[i.date] = i; });
  const out = [];

  income.forEach(row => {
    const dt = row.date;
    const b = bsByDate[dt], ic = incByDate[dt];
    if (!b || !ic) return;

    const d = getFundDate(row);   // <<< release date from the income row
    const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
    const tEpoch = toEpoch(key);
    const price  = findNearestPriceValue(tEpoch, px);
    if (!price) return;

    const snap = {
      totalCurrentAssets: b.totalCurrentAssets,
      totalCurrentLiabilities: b.totalCurrentLiabilities,
      retainedEarnings: b.retainedEarnings,
      ebit: ic.ebit ?? ic.operatingIncome ?? null,
      totalAssets: b.totalAssets,
      revenue: ic.revenue,
      commonStockSharesOutstanding:
        row.weightedAverageShsOutDil ?? row.weightedAverageShsOut ?? null,
    };
    const Z = altmanZScore(snap, price);
    out.push({ time: tEpoch, value: Z });
  });

  return out.sort((a, b) => a.time - b.time);
}


export function computeSYO({ income, cashflow, px, lagDays, interval }) {
  const epsQ = forwardFillFundRows(income, ["eps", "revenue"]);
  const cfQ = forwardFillIncomeField(cashflow, "freeCashFlow");
  if (!epsQ.length || !cfQ.length || !px?.length) return [];

  // Step 1: Build TTM FCF series keyed by quarter (YYYY-Qn) for alignment with income
  const ttmFCF = {};
  for (let i = 3; i < cfQ.length; i++) {
    const last4 = cfQ.slice(i - 3, i + 1);
    if (!last4.every(e => e.freeCashFlow != null && Number.isFinite(e.freeCashFlow))) continue;
    const qKey = dateToQuarterKey(cfQ[i].date);
    ttmFCF[qKey] = last4.reduce((s, e) => s + e.freeCashFlow, 0);
  }

  // TTM revenue by index for smoothed revenue growth (TTM vs prior TTM)
  const ttmRevenue = [];
  for (let i = 3; i < epsQ.length; i++) {
    const last4 = epsQ.slice(i - 3, i + 1);
    if (!last4.every(e => e.revenue != null && Number.isFinite(e.revenue))) {
      ttmRevenue[i] = null;
      continue;
    }
    ttmRevenue[i] = last4.reduce((s, e) => s + e.revenue, 0);
  }

  // Step 2: Build quarterly fundamental "staircase" - carry forward until next quarter
  const fundStairs = [];
  for (let i = 0; i < epsQ.length; i++) {
    const row = epsQ[i];
    const relDate = getFundDate(row);
    relDate.setDate(relDate.getDate() + (lagDays || 0));
    const key = alignDateToInterval(relDate.toISOString().split("T")[0], interval);
    const t = toEpoch(key);

    const sh = row.weightedAverageShsOutDil ?? row.weightedAverageShsOut ?? null;
    if (!sh) continue;

    // Smoothed revenue growth: TTM revenue vs TTM same period prior year (less noisy than single-quarter YoY)
    let rev_g = null;
    if (i >= 4 && ttmRevenue[i] != null && ttmRevenue[i - 4] != null && ttmRevenue[i - 4] > 0) {
      rev_g = ttmRevenue[i] / ttmRevenue[i - 4] - 1;
    }

    const qKey = dateToQuarterKey(row.date);
    const fcf_ttm = ttmFCF[qKey] ?? null;
    if (row.eps != null && fcf_ttm != null) {
      fundStairs.push({
        time: t,
        eps: row.eps,
        fcf_ttm: fcf_ttm,
        shares: sh,
        rev_g: rev_g ?? 0,
      });
    }
  }

  if (!fundStairs.length) return [];

  // Step 3: Build daily series by carrying forward fundamentals and updating with daily price
  const dailyPts = [];
  let fundIdx = 0;

  for (const bar of px) {
    // Advance to most recent fundamental data available at or before this price bar
    while (fundIdx + 1 < fundStairs.length && fundStairs[fundIdx + 1].time <= bar.time) {
      fundIdx++;
    }

    const fund = fundStairs[fundIdx];
    if (!fund || !Number.isFinite(bar.value) || bar.value <= 0) continue;

    // Calculate daily EY and FCFY using current price
    const ey = fund.eps / bar.value;
    const mcap = bar.value * fund.shares;
    const fcfy = (fund.fcf_ttm != null && mcap > 0) ? (fund.fcf_ttm / mcap) : null;

    if (Number.isFinite(ey) && Number.isFinite(fcfy)) {
      dailyPts.push({
        time: bar.time,
        ey,
        fcfy,
        rev_g: fund.rev_g, // Revenue growth stays quarterly (carried forward)
      });
    }
  }

  if (!dailyPts.length) return [];

  // Step 4: Build historical series with time stamps for rolling window filtering
  const historicalEYWithTime = fundStairs.map(f => {
    const pxAtFund = findNearestPriceValue(f.time, px);
    const ey = pxAtFund && pxAtFund > 0 ? f.eps / pxAtFund : null;
    return ey != null && isFinite(ey) ? { time: f.time, value: ey } : null;
  }).filter(v => v != null);

  const historicalFCFYWithTime = fundStairs.map(f => {
    const pxAtFund = findNearestPriceValue(f.time, px);
    const sh = f.shares;
    if (!pxAtFund || !sh || pxAtFund <= 0) return null;
    const mcap = pxAtFund * sh;
    const fcfy = f.fcf_ttm != null && mcap > 0 ? f.fcf_ttm / mcap : null;
    return fcfy != null && isFinite(fcfy) ? { time: f.time, value: fcfy } : null;
  }).filter(v => v != null);

  const historicalRevGWithTime = fundStairs.map(f => ({
    time: f.time,
    value: f.rev_g
  })).filter(v => v != null && isFinite(v.value));

  // Rolling window: 5 years in seconds
  const ROLLING_WINDOW_SEC = 5 * 365 * 24 * 3600;

  // Percentile (linear interpolation); p in [0, 100]
  function percentile(sorted, p) {
    const n = sorted.length;
    if (!n) return 0;
    if (n === 1) return sorted[0];
    const idx = (p / 100) * (n - 1);
    const lo = Math.floor(idx), hi = Math.ceil(idx);
    if (lo === hi) return sorted[lo];
    return sorted[lo] + (idx - lo) * (sorted[hi] - sorted[lo]);
  }

  // Rank function: winsorize at 2nd–98th percentile then rank (so extremes don't compress the distribution)
  function rankValue(value, historicalWithTime, currentTime) {
    if (!historicalWithTime.length || !Number.isFinite(value)) return 0.5;
    
    const window = historicalWithTime.filter(h => 
      h.time >= currentTime - ROLLING_WINDOW_SEC && 
      h.time <= currentTime
    );
    
    const rawValues = window.length >= 4
      ? window.map(h => h.value).sort((a, b) => a - b)
      : historicalWithTime.map(h => h.value).sort((a, b) => a - b);
    
    if (!rawValues.length) return 0.5;
    const n = rawValues.length;
    if (n === 1) return value <= rawValues[0] ? 0 : 1;

    const p2 = percentile(rawValues, 2);
    const p98 = percentile(rawValues, 98);
    const valueWinsorized = clamp(value, p2, p98);

    if (valueWinsorized <= rawValues[0]) return 0;
    if (valueWinsorized >= rawValues[n - 1]) return 1;
    let lo = 0, hi = n - 1;
    while (lo <= hi) {
      const m = (lo + hi) >> 1;
      (rawValues[m] < valueWinsorized) ? (lo = m + 1) : (hi = m - 1);
    }
    return lo / (n - 1);
  }

  // Step 5: Calculate daily SYO using daily EY/FCFY but ranked against 5-year rolling window
  return dailyPts.map(p => {
    const ey_rank = rankValue(p.ey, historicalEYWithTime, p.time);
    const fcfy_rank = rankValue(p.fcfy, historicalFCFYWithTime, p.time);
    const rev_g_rank = rankValue(p.rev_g, historicalRevGWithTime, p.time);

    return {
      time: p.time,
      value: clamp(
        100 * (0.40 * ey_rank + 0.40 * fcfy_rank + 0.20 * rev_g_rank),
        0,
        100
      )
    };
  }).sort((x, y) => x.time - y.time);
}


export function computeRMO(income, px, interval, lagDays) {
  const revTTM = buildTTM(income, "revenue", lagDays, interval);
  if (revTTM.length < 5 || px.length < 2) return [];
  const out = [];
  for (let i = 4; i < revTTM.length; i++) {
    const t  = revTTM[i].time;
    const t4 = revTTM[i - 4].time;
    const R1 = revTTM[i].value, R0 = revTTM[i - 4].value;
    if (!R1 || !R0) continue;
    const g = R1 / R0 - 1;
    const p1 = findNearestPriceValue(t, px);
    const p0 = findNearestPriceValue(t4, px);
    if (!p1 || !p0) continue;
    const p = p1 / p0 - 1;
    // RMO = 100 * (revenue_growth - price_growth)
    // Normalized to [-100, 100] range
    const rmo = 100 * (g - p);
    if (Number.isFinite(rmo)) {
      // Clamp to [-100, 100] range
      const normalizedRmo = clamp(rmo, -100, 100);
      out.push({ time: t, value: normalizedRmo });
    }
  }
  return out;
}

// NIMO (Net Income Momentum Oscillator): Compares TTM net income growth vs price growth
// If net income grows faster than price, indicates undervaluation
export function computeNIMO(income, px, interval, lagDays) {
  const niTTM = buildTTM(income, "netIncome", lagDays, interval);
  if (niTTM.length < 5 || px.length < 2) return [];
  const out = [];
  for (let i = 4; i < niTTM.length; i++) {
    const t  = niTTM[i].time;
    const t4 = niTTM[i - 4].time;
    const NI1 = niTTM[i].value, NI0 = niTTM[i - 4].value;
    
    // Skip if either value is missing or zero (net income can be negative, so we need to handle that)
    if (!Number.isFinite(NI1) || !Number.isFinite(NI0) || NI0 === 0) continue;
    
    // Net income growth rate
    const niGrowth = NI1 / NI0 - 1;
    
    // Price growth over same period
    const p1 = findNearestPriceValue(t, px);
    const p0 = findNearestPriceValue(t4, px);
    if (!p1 || !p0 || p0 <= 0) continue;
    
    const priceGrowth = p1 / p0 - 1;
    
    // NIMO = 100 * (netIncome_growth - price_growth)
    // Normalized to [-100, 100] range
    // Positive means net income growing faster than price (undervalued)
    // Negative means price growing faster than net income (overvalued)
    const nimo = 100 * (niGrowth - priceGrowth);
    if (Number.isFinite(nimo)) {
      // Clamp to [-100, 100] range
      const normalizedNimo = clamp(nimo, -100, 100);
      out.push({ time: t, value: normalizedNimo });
    }
  }
  
  return out;
}

// TAMO (Total Assets Momentum Oscillator): Compares quarterly total assets growth vs price growth
// If total assets grow faster than price, indicates undervaluation
export function computeTAMO(balanceSheet, px, interval, lagDays) {
  if (!balanceSheet?.length || !px?.length) return [];

  const bsFilled = forwardFillIncomeField(balanceSheet, "totalAssets");
  const taQ = shiftAndMap(
    bsFilled,
    e => e.totalAssets ?? null,
    lagDays || 0,
    interval
  );
  
  if (taQ.length < 5 || px.length < 2) return [];
  
  const out = [];
  // Compare year-over-year (4 quarters) similar to RMO
  for (let i = 4; i < taQ.length; i++) {
    const t  = taQ[i].time;
    const t4 = taQ[i - 4].time;
    const TA1 = taQ[i].value, TA0 = taQ[i - 4].value;
    
    if (!TA1 || !TA0 || TA0 <= 0) continue;
    
    // Total assets growth rate
    const taGrowth = TA1 / TA0 - 1;
    
    // Price growth over same period
    const p1 = findNearestPriceValue(t, px);
    const p0 = findNearestPriceValue(t4, px);
    if (!p1 || !p0 || p0 <= 0) continue;
    
    const priceGrowth = p1 / p0 - 1;
    
    // TAMO = 100 * (totalAssets_growth - price_growth)
    // Normalized to [-100, 100] range
    // Positive means assets growing faster than price (undervalued)
    // Negative means price growing faster than assets (overvalued)
    const tamo = 100 * (taGrowth - priceGrowth);
    if (Number.isFinite(tamo)) {
      // Clamp to [-100, 100] range
      const normalizedTamo = clamp(tamo, -100, 100);
      out.push({ time: t, value: normalizedTamo });
    }
  }
  
  return out;
}

export function computeCFAO(cashflow, interval, lagDays, opts = {}) {
  const { normalize = false, lookback = 24 } = opts; // 24 quarter prints ≈ 6 years
  const fcfTTM = buildTTM(cashflow, "freeCashFlow", lagDays, interval);
  if (fcfTTM.length < 9) return [];

  // step 1: growth of TTM FCF vs 4Q ago
  const growth = [];
  for (let i = 4; i < fcfTTM.length; i++) {
    const now = fcfTTM[i].value, prev = fcfTTM[i - 4].value;
    if (!isFinite(now) || !isFinite(prev) || prev === 0) continue;
    growth.push({ time: fcfTTM[i].time, value: now / prev - 1 });
  }
  if (growth.length < 5) return [];

  // step 2: CFAO = 100 * (current growth - avg of last 4 growths)
  const raw = [];
  const mean = a => a.length ? a.reduce((s, x) => s + x, 0) / a.length : 0;
  for (let i = 4; i < growth.length; i++) {
    const gNow = growth[i].value;
    const gAvg = mean(growth.slice(i - 4, i).map(x => x.value));
    raw.push({ time: growth[i].time, value: 100 * (gNow - gAvg) });
  }
  if (!normalize) return raw;

  // step 3: rolling percentile rank → 0..100
  function percentileRank(window, v) {
    const a = window.filter(x => isFinite(x)).slice().sort((x, y) => x - y);
    const n = a.length;
    if (!n) return 50;
    if (n === 1) return v < a[0] ? 0 : 100;
    // lower_bound
    let lo = 0, hi = n - 1;
    if (v <= a[0]) return 0;
    if (v >= a[hi]) return 100;
    while (lo <= hi) { const m = (lo + hi) >> 1; (a[m] < v) ? (lo = m + 1) : (hi = m - 1); }
    // scale to 0..100 using rank/(n-1)
    return 100 * (lo / (n - 1));
  }

  const out = [];
  const vals = raw.map(p => p.value);
  for (let i = 0; i < raw.length; i++) {
    const start = Math.max(0, i - lookback + 1);
    const window = vals.slice(start, i + 1);
    out.push({ time: raw[i].time, value: percentileRank(window, raw[i].value) });
  }
  return out;
}

export function computeVMZ(metrics, interval, lagDays, px = null, income = null, balanceSheet = null) {
  // New daily-responsive version: calculate EV using daily stock prices
  // EV = Market Cap + Total Debt - Cash
  // Market Cap = Daily Price × Shares Outstanding
  // EV/EBITDA = EV / TTM EBITDA
  
  if (px && income && balanceSheet) {
    // Build quarterly series for fundamentals
    const sharesQ = (income || [])
      .map(e => {
        const d = new Date(e.fillingDate || e.acceptedDate || e.date);
        d.setDate(d.getDate() + (lagDays || 0));
        const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
        const sh = e.weightedAverageShsOutDil ?? e.weightedAverageShsOut ?? null;
        return (sh != null && isFinite(sh)) ? { time: toEpoch(key), value: sh } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.time - b.time);

    // Build quarterly series for debt and cash from balance sheet
    const debtCashQ = (balanceSheet || [])
      .map(e => {
        const d = new Date(e.fillingDate || e.acceptedDate || e.date);
        d.setDate(d.getDate() + (lagDays || 0));
        const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
        const t = toEpoch(key);
        // Try multiple field names for debt and cash
        const debt = e.totalDebt ?? e.totalLiabilities ?? 
                     (e.longTermDebt ? (e.longTermDebt + (e.shortTermDebt || 0)) : null) ?? null;
        const cash = e.cashAndCashEquivalents ?? e.cashAndShortTermInvestments ?? 
                     e.totalCash ?? e.cash ?? null;
        return (debt != null || cash != null) ? { time: t, debt: debt ?? 0, cash: cash ?? 0 } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.time - b.time);

    // Build TTM EBITDA series
    const ebitdaTTM = buildTTM(income, "ebitda", lagDays, interval);

    if (!sharesQ.length || !ebitdaTTM.length || !px?.length) {
      // Fallback to old method if we don't have required data
      return computeVMZLegacy(metrics, interval, lagDays);
    }

    // Helper to get most recent quarterly value at or before a time
    const getLatestQ = (series, t) => {
      let best = null;
      for (let i = series.length - 1; i >= 0; i--) {
        if (series[i].time <= t) {
          best = series[i];
          break;
        }
      }
      return best;
    };

    // Build daily EV/EBITDA series
    const evEbitdaSeries = [];
    for (const pricePt of px) {
      const sharesPt = getLatestQ(sharesQ, pricePt.time);
      const debtCashPt = getLatestQ(debtCashQ, pricePt.time);
      const ebitdaPt = getLatestQ(ebitdaTTM, pricePt.time);

      if (!sharesPt || !debtCashPt || !ebitdaPt) continue;

      const price = pricePt.value;
      const shares = sharesPt.value;
      const debt = debtCashPt.debt;
      const cash = debtCashPt.cash;
      const ebitda = ebitdaPt.value;

      if (!(price > 0) || !(shares > 0) || !(ebitda > 0)) continue;

      // Calculate EV = Market Cap + Debt - Cash
      const marketCap = price * shares;
      const ev = marketCap + debt - cash;

      if (ev <= 0) continue;

      // Calculate EV/EBITDA
      const evEbitda = ev / ebitda;
      if (isFinite(evEbitda) && evEbitda > 0) {
        evEbitdaSeries.push({ time: pricePt.time, value: evEbitda });
      }
    }

    if (evEbitdaSeries.length < 8) {
      // Fallback if we don't have enough daily data
      return computeVMZLegacy(metrics, interval, lagDays);
    }

    // Compute z-score on daily EV/EBITDA series
    const FIVE_Y = 5 * 365 * 24 * 3600;
    const out = [];
    const median = a => {
      if (!a.length) return null;
      const s = a.slice().sort((x, y) => x - y);
      const m = Math.floor(s.length / 2);
      return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
    };

    for (let i = 0; i < evEbitdaSeries.length; i++) {
      const t = evEbitdaSeries[i].time;
      const window = evEbitdaSeries.filter(p => p.time >= t - FIVE_Y && p.time <= t).map(p => p.value);
      if (window.length < 8) continue;
      const med = median(window);
      const sdv = stdev(window);
      if (!sdv) continue;
      const z = (evEbitdaSeries[i].value - med) / sdv;
      out.push({ time: t, value: z });
    }
    return out;
  }

  // Legacy method: use quarterly EV/EBITDA from metrics
  return computeVMZLegacy(metrics, interval, lagDays);
}

// Legacy VMZ calculation using quarterly metrics
function computeVMZLegacy(metrics, interval, lagDays) {
  const multSeries = (metrics || [])
    .map(e => {
      const d = new Date(e.fillingDate || e.acceptedDate || e.date); d.setDate(d.getDate() + lagDays);
      const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
      const t = toEpoch(key);
      const m = e.enterpriseValueMultiple ?? e.evToEbitda ?? null;
      return (m != null && isFinite(m)) ? { time: t, value: m } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.time - b.time);

  if (multSeries.length < 2) return [];
  const FIVE_Y = 5 * 365 * 24 * 3600;
  const out = [];
  const median = a => {
    if (!a.length) return null;
    const s = a.slice().sort((x, y) => x - y);
    const m = Math.floor(s.length / 2);
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
  };
  for (let i = 0; i < multSeries.length; i++) {
    const t = multSeries[i].time;
    const window = multSeries.filter(p => p.time >= t - FIVE_Y && p.time <= t).map(p => p.value);
    if (window.length < 8) continue;
    const med = median(window);
    const sdv = stdev(window);
    if (!sdv) continue;
    const z = (multSeries[i].value - med) / sdv;
    out.push({ time: t, value: z });
  }
  return out;
}

/* Annual aggregation from quarterlies */
export function buildAnnualFromQuarterlies({ incomeQ, bsQ, cfQ }) {
  const byYear = {};

  (incomeQ || []).forEach(r => {
    const yr = r.calendarYear || (r.date ? r.date.slice(0, 4) : null);
    if (!yr) return;
    byYear[yr] = byYear[yr] || { year: yr };

    const d = getFundDate(r);
    const stamp = d.getTime();
    if (!byYear[yr]._iStamp || stamp >= byYear[yr]._iStamp) {
      byYear[yr]._iStamp = stamp;
      byYear[yr]._relDate = d.toISOString().split("T")[0];   // <<< release date for year

      byYear[yr].shares = r.weightedAverageShsOutDil ?? r.weightedAverageShsOut ?? byYear[yr].shares ?? null;
      byYear[yr].revenue = r.revenue ?? byYear[yr].revenue ?? null;
      byYear[yr].grossProfit = r.grossProfit ?? byYear[yr].grossProfit ?? null;
      byYear[yr].netIncome = r.netIncome ?? byYear[yr].netIncome ?? null;
      byYear[yr].totalAssets = r.totalAssets ?? byYear[yr].totalAssets ?? null;
    }
  });

  (bsQ || []).forEach(r => {
    const yr = r.calendarYear || (r.date ? r.date.slice(0, 4) : null);
    if (!yr) return;
    byYear[yr] = byYear[yr] || { year: yr };

    const d = getFundDate(r);
    const stamp = d.getTime();
    if (!byYear[yr]._bStamp || stamp >= byYear[yr]._bStamp) {
      byYear[yr]._bStamp = stamp;
      byYear[yr]._relDate = byYear[yr]._relDate || d.toISOString().split("T")[0];

      byYear[yr].totalAssets = r.totalAssets ?? byYear[yr].totalAssets ?? null;
      byYear[yr].totalCurrentAssets = r.totalCurrentAssets ?? byYear[yr].totalCurrentAssets ?? null;
      byYear[yr].totalCurrentLiabilities = r.totalCurrentLiabilities ?? byYear[yr].totalCurrentLiabilities ?? null;
      byYear[yr].longTermDebt = r.longTermDebt ?? byYear[yr].longTermDebt ?? null;
      byYear[yr].retainedEarnings = r.retainedEarnings ?? byYear[yr].retainedEarnings ?? null;
    }
  });

  const cfByYear = {};
  (cfQ || []).forEach(r => {
    const yr = r.calendarYear || (r.date ? r.date.slice(0, 4) : null);
    if (!yr) return;
    const ocf = r.netCashProvidedByOperatingActivities
      ?? r.operatingCashFlow
      ?? r.netCashProvidedByOperatingActivitiesUSD
      ?? null;
    if (ocf != null) cfByYear[yr] = (cfByYear[yr] || 0) + ocf;
  });

  Object.keys(cfByYear).forEach(yr => {
    byYear[yr] = byYear[yr] || { year: yr };
    byYear[yr].cfo = cfByYear[yr];
  });

  return Object.values(byYear)
    .filter(r => r.year && /^\d{4}$/.test(String(r.year)))
    .sort((a, b) => Number(a.year) - Number(b.year))
    .map(r => {
      const dStr = r._relDate || `${r.year}-12-31`;  // <<< final "release-ish" date
      return {
        year: Number(r.year),
        date: dStr,
        shares: r.shares ?? null,
        revenue: r.revenue ?? null,
        grossProfit: r.grossProfit ?? null,
        netIncome: r.netIncome ?? null,
        totalAssets: r.totalAssets ?? null,
        totalCurrentAssets: r.totalCurrentAssets ?? null,
        totalCurrentLiabilities: r.totalCurrentLiabilities ?? null,
        longTermDebt: r.longTermDebt ?? null,
        retainedEarnings: r.retainedEarnings ?? null,
        cfo: r.cfo ?? null,
      };
    });
}


/**
 * Quarterly-to-annual Piotroski F-Score series (using your annual rollup)
 * Returns [{ time, value }], time aligned to interval + lagDays.
 */
export function buildFScoreTTMSeries(incomeQ, cashflowQ, balanceSheetQ, interval, lagDays) {
  const annual = buildAnnualFromQuarterlies({ incomeQ, bsQ: balanceSheetQ, cfQ: cashflowQ });
  if (!annual || annual.length < 2) return [];

  const safeDiv = (a, b) => (b && isFinite(a) && isFinite(b)) ? (a / b) : null;

  const out = [];
  for (let i = 1; i < annual.length; i++) {
    const curr = annual[i], prev = annual[i - 1];

    // Profitability
    const roaNow  = safeDiv(curr.netIncome, curr.totalAssets);
    const roaPrev = safeDiv(prev.netIncome, prev.totalAssets);
    const cfoNow  = curr.cfo ?? null;
    const accrual = (cfoNow != null && curr.netIncome != null) ? (cfoNow > curr.netIncome) : null;

    // Leverage/Liquidity
    const levNow  = safeDiv(curr.longTermDebt, curr.totalAssets);
    const levPrev = safeDiv(prev.longTermDebt, prev.totalAssets);
    const crNow   = safeDiv(curr.totalCurrentAssets, curr.totalCurrentLiabilities);
    const crPrev  = safeDiv(prev.totalCurrentAssets, prev.totalCurrentLiabilities);
    const shrNow  = curr.shares ?? null;
    const shrPrev = prev.shares ?? null;

    // Operating efficiency
    const gmNow   = (curr.grossProfit != null && curr.revenue) ? (curr.grossProfit / curr.revenue) : null;
    const gmPrev  = (prev.grossProfit != null && prev.revenue) ? (prev.grossProfit / prev.revenue) : null;
    const atNow   = safeDiv(curr.revenue, curr.totalAssets);
    const atPrev  = safeDiv(prev.revenue, prev.totalAssets);

    // 9 binary signals
    let score = 0;
    // Profitability (4)
    if (roaNow != null && roaNow > 0) score++;                  // ROA > 0
    if (cfoNow != null && cfoNow > 0) score++;                  // CFO > 0
    if (roaNow != null && roaPrev != null && roaNow > roaPrev) score++;     // ΔROA > 0
    if (accrual != null && accrual) score++;                    // CFO > NetIncome

    // Leverage/Liquidity (3)
    if (levNow != null && levPrev != null && levNow < levPrev) score++;     // ↓ leverage
    if (crNow != null && crPrev != null && crNow > crPrev) score++;         // ↑ current ratio
    if (shrNow != null && shrPrev != null && shrNow <= shrPrev) score++;    // no dilution

    // Operating efficiency (2)
    if (gmNow != null && gmPrev != null && gmNow > gmPrev) score++;         // ↑ gross margin
    if (atNow != null && atPrev != null && atNow > atPrev) score++;         // ↑ asset turnover

        // Align to interval + lag using release date
    const d = getFundDate(curr);
    d.setDate(d.getDate() + (lagDays || 0));
    const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
    const t   = toEpoch(key);

    out.push({ time: t, value: score });

  }
  return out;
}

/* =========================================================================
   Extra computed series used in StockChart
   ========================================================================= */

// CCE (normalized 0–100): percentile of CFO_TTM / Revenue_TTM vs last ~5y of prints
export function computeCCE(income, cashflow, interval, lagDays, opts = {}) {
  const lookback = opts.lookback ?? 20; // ~5 years of quarterlies
  // Build TTM series
  const cfoTTM = buildTTM(cashflow, "netCashProvidedByOperatingActivities", lagDays, interval)
    .map(p => ({ time: p.time, value: p.value ?? 0 }));
  const revTTM = buildTTM(income, "revenue", lagDays, interval);

  if (!cfoTTM.length || !revTTM.length) return [];

  // Align by nearest within ~200 days and compute raw ratio
  const raw = [];
  for (const r of revTTM) {
    // nearest CFO within 200 days
    const c = nearestPoint(cfoTTM, r.time, 60 * 60 * 24 * 200);
    if (!c || !r.value || !Number.isFinite(c.value) || !Number.isFinite(r.value)) continue;
    const ratio = c.value / r.value; // 0.18 = 18%
    if (!Number.isFinite(ratio)) continue;
    raw.push({ time: r.time, value: ratio });
  }
  if (raw.length === 0) return [];

  // Rolling percentile rank to 0..100
  function percentileRank(window, v) {
    const a = window.filter(Number.isFinite).slice().sort((x, y) => x - y);
    const n = a.length;
    if (n === 0) return 50;
    if (n === 1) return v < a[0] ? 0 : 100;
    if (v <= a[0]) return 0;
    if (v >= a[n - 1]) return 100;
    // lower_bound
    let lo = 0, hi = n - 1;
    while (lo <= hi) { const m = (lo + hi) >> 1; (a[m] < v) ? (lo = m + 1) : (hi = m - 1); }
    return 100 * (lo / (n - 1));
  }

  const out = [];
  const vals = raw.map(p => p.value);
  for (let i = 0; i < raw.length; i++) {
    const start = Math.max(0, i - lookback + 1);
    const window = vals.slice(start, i + 1);
    out.push({ time: raw[i].time, value: percentileRank(window, vals[i]) });
  }
  return out;
}

// OEY (normalized 0–100): daily FCF yield 100*FCF_TTM/MCap, then percentile rank vs ~5y rolling window of those daily values
export function computeOEY(cashflow, income, px, interval, lagDays, opts = {}) {
  const LOOKBACK_SEC = opts.lookbackSec ?? 5 * 365 * 24 * 3600;
  const minPts = opts.minPts ?? 8;

  const fcfTTM = buildTTM(cashflow, "freeCashFlow", lagDays, interval);
  if (!fcfTTM.length || !px?.length) return [];

  const sharesQ = (income || [])
    .map((e) => {
      const d = new Date(e.fillingDate || e.acceptedDate || e.date);
      d.setDate(d.getDate() + (lagDays || 0));
      const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
      const sh = e.weightedAverageShsOutDil ?? e.weightedAverageShsOut ?? null;
      return sh != null && isFinite(sh) ? { time: toEpoch(key), value: sh } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.time - b.time);

  const getLatestQ = (series, t) => {
    let best = null;
    for (let i = series.length - 1; i >= 0; i--) {
      if (series[i].time <= t) {
        best = series[i];
        break;
      }
    }
    return best;
  };

  function percentileRank(windowVals, v) {
    const a = windowVals.filter(Number.isFinite).slice().sort((x, y) => x - y);
    const n = a.length;
    if (!n) return 50;
    if (n === 1) return v < a[0] ? 0 : 100;
    if (v <= a[0]) return 0;
    if (v >= a[n - 1]) return 100;
    let lo = 0;
    let hi = n - 1;
    while (lo <= hi) {
      const m = (lo + hi) >> 1;
      if (a[m] < v) lo = m + 1;
      else hi = m - 1;
    }
    return 100 * (lo / (n - 1));
  }

  const priceBars = [...px]
    .filter((p) => p && Number.isFinite(p.time) && Number.isFinite(p.value) && p.value > 0)
    .sort((a, b) => a.time - b.time);

  const rawDaily = [];
  for (const bar of priceBars) {
    const fcfPt = getLatestQ(fcfTTM, bar.time);
    const shPt = getLatestQ(sharesQ, bar.time);
    if (!fcfPt || !shPt) continue;
    const mcap = bar.value * shPt.value;
    if (!(mcap > 0)) continue;
    const oeyPct = 100 * (fcfPt.value / mcap);
    if (Number.isFinite(oeyPct)) rawDaily.push({ time: bar.time, value: oeyPct });
  }
  if (!rawDaily.length) return [];

  const out = [];
  for (let i = 0; i < rawDaily.length; i++) {
    const t = rawDaily[i].time;
    const v = rawDaily[i].value;
    const windowVals = rawDaily
      .filter((p) => p.time >= t - LOOKBACK_SEC && p.time <= t)
      .map((p) => p.value)
      .filter(Number.isFinite);
    if (windowVals.length < minPts) continue;
    out.push({ time: t, value: percentileRank(windowVals, v) });
  }
  return out;
}

// CFAO Delta (ΔCFAO): first-difference of CFAO — deviation/momentum in cash flow acceleration
// Positive = CFAO increasing = improving cash flow momentum = good (stock going up)
// Negative = CFAO decreasing = deteriorating cash flow momentum = bad (stock sold or going down)
// Modeled on computeVMO (Valuation Momentum Δz)
export function computeCFAODelta(cfaoSeries, opts = {}) {
  if (!cfaoSeries?.length) return [];

  const {
    smoothBefore = 2,  // Smooth CFAO with 2-period EMA before diff (quarterly data)
    smoothAfter = 0,   // No smoothing after (0 = disable) — quarterly already sparse
    diffWindow = 1,    // 1 = quarter-over-quarter change
  } = opts;

  // Step 1: Optionally smooth CFAO before differentiation
  let smoothed = cfaoSeries;
  if (smoothBefore > 1 && cfaoSeries.length > smoothBefore) {
    const k = 2 / (smoothBefore + 1);
    smoothed = [];
    let ema = cfaoSeries[0].value;
    smoothed.push({ time: cfaoSeries[0].time, value: ema });
    for (let i = 1; i < cfaoSeries.length; i++) {
      if (Number.isFinite(cfaoSeries[i].value)) {
        ema = k * cfaoSeries[i].value + (1 - k) * ema;
        smoothed.push({ time: cfaoSeries[i].time, value: ema });
      } else {
        smoothed.push({ time: cfaoSeries[i].time, value: ema });
      }
    }
  }

  // Step 2: Compute first difference (ΔCFAO)
  const out = [];
  for (let i = diffWindow; i < smoothed.length; i++) {
    const prev = smoothed[i - diffWindow];
    const curr = smoothed[i];
    if (!prev || !curr || !Number.isFinite(prev.value) || !Number.isFinite(curr.value)) continue;
    out.push({ time: curr.time, value: curr.value - prev.value });
  }

  // Step 3: Optionally smooth after differentiation
  if (smoothAfter > 1 && out.length > smoothAfter) {
    const k = 2 / (smoothAfter + 1);
    const smoothedOut = [];
    let ema = out[0].value;
    smoothedOut.push({ time: out[0].time, value: ema });
    for (let i = 1; i < out.length; i++) {
      ema = k * out[i].value + (1 - k) * ema;
      smoothedOut.push({ time: out[i].time, value: ema });
    }
    return smoothedOut;
  }

  return out;
}

// VMO: first-difference of VMZ (Δz) with noise reduction
export function computeVMO(vmzSeries, opts = {}) {
  if (!vmzSeries?.length) return [];
  
  const {
    // Smooth VMZ before differentiation (EMA period in days)
    smoothBefore = 5,  // Smooth VMZ with 5-day EMA before taking derivative
    // Smooth VMO after differentiation (EMA period in days)
    smoothAfter = 3,   // Smooth VMO with 3-day EMA after taking derivative
    // Alternative: use longer time difference instead of consecutive
    diffWindow = 1,    // Number of periods to difference (1 = consecutive, 5 = 5-day difference)
    // Minimum change threshold (optional, set to 0 to disable)
    minChange = 0,     // Filter out changes smaller than this (in z-score units)
  } = opts;
  
  // Step 1: Optionally smooth VMZ before differentiation
  let smoothedVMZ = vmzSeries;
  if (smoothBefore > 1 && vmzSeries.length > smoothBefore) {
    const k = 2 / (smoothBefore + 1);
    smoothedVMZ = [];
    let ema = vmzSeries[0].value;
    smoothedVMZ.push({ time: vmzSeries[0].time, value: ema });
    
    for (let i = 1; i < vmzSeries.length; i++) {
      if (Number.isFinite(vmzSeries[i].value)) {
        ema = k * vmzSeries[i].value + (1 - k) * ema;
        smoothedVMZ.push({ time: vmzSeries[i].time, value: ema });
      } else {
        smoothedVMZ.push({ time: vmzSeries[i].time, value: ema }); // Carry forward
      }
    }
  }
  
  // Step 2: Compute first difference (or longer window difference)
  const out = [];
  for (let i = diffWindow; i < smoothedVMZ.length; i++) {
    const prev = smoothedVMZ[i - diffWindow];
    const curr = smoothedVMZ[i];
    
    if (!prev || !curr || !Number.isFinite(prev.value) || !Number.isFinite(curr.value)) {
      continue;
    }
    
    const diff = curr.value - prev.value;
    
    // Filter out small changes if threshold is set
    if (minChange > 0 && Math.abs(diff) < minChange) {
      continue;
    }
    
    out.push({ time: curr.time, value: diff });
  }
  
  // Step 3: Optionally smooth VMO after differentiation
  if (smoothAfter > 1 && out.length > smoothAfter) {
    const k = 2 / (smoothAfter + 1);
    const smoothed = [];
    let ema = out[0].value;
    smoothed.push({ time: out[0].time, value: ema });
    
    for (let i = 1; i < out.length; i++) {
      ema = k * out[i].value + (1 - k) * ema;
      smoothed.push({ time: out[i].time, value: ema });
    }
    return smoothed;
  }
  
  return out;
}

// Revenue Surprise: simple % surprise (actual vs estimate) per quarter
export function computeRevenueSurprise(earnings, interval, lagDays, opts = {}) {
  const revFloor = opts.revFloor != null ? opts.revFloor : 1e6; // avoid div by zero
  const winsor = opts.winsor != null ? opts.winsor : 100;        // clamp ±winsor %
  const src = Array.isArray(earnings) ? earnings : [];
  const out = [];
  for (const e of src) {
    if (!e?.date) continue;
    const actual = e.revenue;
    const estimate = e.revenueEstimated;
    if (!Number.isFinite(actual) || !Number.isFinite(estimate)) continue;
    const denom = Math.max(Math.abs(estimate), revFloor);
    let surprisePct = 100 * ((actual - estimate) / denom);
    if (winsor > 0) {
      if (surprisePct > winsor) surprisePct = winsor;
      if (surprisePct < -winsor) surprisePct = -winsor;
    }
    const d = new Date(e.fillingDate || e.acceptedDate || e.date);
    d.setDate(d.getDate() + (lagDays || 0));
    const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
    out.push({ time: toEpoch(key), value: surprisePct });
  }
  return out.sort((a, b) => a.time - b.time);
}

// ESM: EPS Surprise Momentum (improved version) — kept for reference, not used by app
// Key improvements:
// 1. Calculate momentum of RAW surprises first (preserves magnitude)
// 2. Then normalize the momentum values
// 3. Support year-over-year momentum (4-quarter change) for trend detection
// 4. Rolling average of recent surprises as alternative signal
export function computeESM(earnings, interval, lagDays, opts = {}) {
  const {
    mode = "momentum",          // "surprise" (level), "momentum" (q/q), "yoy" (year-over-year), "rolling" (avg of last N)
    transform = "log",          // "pct" or "log"
    epsFloor = 0.05,            // floor to stabilize tiny estimates
    winsor = 200,               // clamp raw surprise to ±winsor before normalization (0 to disable)
    normalize = "percentile",   // null | "percentile" | "tanh" | "zscore"
    lookback = 20,              // ~5y of quarterlies for percentile
    smooth = 3,                 // EMA smoothing length on the final series (0/1 to disable)
    rollingWindow = 4,          // for mode="rolling": number of quarters to average
    momentumWindow = 1,         // for mode="momentum": 1 = q/q, 4 = yoy
  } = opts;

  if (!Array.isArray(earnings) || !earnings.length) return [];

  // 1) Build raw surprise series (log or pct), time-aligned and lagged
  const raw = [];
  for (const e of earnings) {
    if (!e?.date) continue;
    const a = e.eps, f = e.epsEstimated;
    if (!Number.isFinite(a) || !Number.isFinite(f)) continue;

    let s; // raw surprise
    if (transform === "log") {
      // Symmetric log-change, sign comes from (a - f). Uses abs in ratio so negatives don't explode.
      const ratio = (Math.abs(a) + epsFloor) / (Math.abs(f) + epsFloor);
      s = 100 * Math.sign(a - f) * Math.log(ratio);
    } else {
      // Percent surprise with denominator floor
      const denom = Math.max(Math.abs(f), epsFloor);
      s = 100 * ((a - f) / denom);
    }
    if (!Number.isFinite(s)) continue;

    // Optional winsor to keep a single goofy quarter from wrecking scales
    if (winsor && winsor > 0) {
      if (s >  winsor) s =  winsor;
      if (s < -winsor) s = -winsor;
    }

    const d = new Date(e.fillingDate || e.acceptedDate || e.date);
    d.setDate(d.getDate() + (lagDays || 0));
    const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
    raw.push({ time: toEpoch(key), value: s });
  }
  raw.sort((x, y) => x.time - y.time);
  if (!raw.length) return [];

  // 2) Calculate momentum/rolling average on RAW surprises (preserves magnitude)
  let series = raw;
  if (mode === "momentum") {
    const momo = [];
    for (let i = momentumWindow; i < raw.length; i++) {
      const curr = raw[i].value;
      const prev = raw[i - momentumWindow].value;
      momo.push({ time: raw[i].time, value: curr - prev });
    }
    series = momo;
  } else if (mode === "yoy") {
    // Year-over-year: compare to same quarter 4 quarters ago
    const yoy = [];
    for (let i = 4; i < raw.length; i++) {
      const curr = raw[i].value;
      const prev = raw[i - 4].value;
      yoy.push({ time: raw[i].time, value: curr - prev });
    }
    series = yoy;
  } else if (mode === "rolling") {
    // Rolling average of last N quarters
    const rolling = [];
    for (let i = rollingWindow - 1; i < raw.length; i++) {
      const window = raw.slice(i - rollingWindow + 1, i + 1);
      const avg = window.reduce((sum, p) => sum + p.value, 0) / window.length;
      rolling.push({ time: raw[i].time, value: avg });
    }
    series = rolling;
  }
  // else mode === "surprise": use raw series as-is

  if (!series.length) return [];

  // 3) Normalize AFTER momentum calculation (preserves magnitude information)
  if (normalize === "percentile") {
    const vals = series.map(p => p.value);
    const pr = (window, v) => {
      const a = window.slice().sort((x, y) => x - y);
      const n = a.length;
      if (!n) return 50;
      if (n === 1) return v < a[0] ? 0 : 100;
      if (v <= a[0]) return 0;
      if (v >= a[n-1]) return 100;
      let lo = 0, hi = n - 1;
      while (lo <= hi) { const m = (lo + hi) >> 1; (a[m] < v) ? (lo = m + 1) : (hi = m - 1); }
      return 100 * (lo / (n - 1));
    };
    const out = [];
    for (let i = 0; i < series.length; i++) {
      const start = Math.max(0, i - lookback + 1);
      out.push({
        time: series[i].time,
        value: pr(vals.slice(start, i + 1), vals[i])
      });
    }
    series = out;
  } else if (normalize === "zscore") {
    // Z-score normalization: (value - mean) / std
    const vals = series.map(p => p.value);
    const mean = vals.reduce((s, x) => s + x, 0) / vals.length;
    const variance = vals.reduce((s, x) => s + (x - mean) ** 2, 0) / (vals.length - 1);
    const std = Math.sqrt(variance);
    if (std > 0) {
      series = series.map(p => ({ time: p.time, value: (p.value - mean) / std }));
    }
  } else if (normalize === "tanh") {
    // Squash to roughly ±100 using tanh
    series = series.map(p => ({ time: p.time, value: 100 * Math.tanh(p.value / 100) }));
  }

  // 4) Optional smoothing (simple EMA) to cut jitter further
  if (smooth && smooth > 1 && series.length) {
    const k = 2 / (smooth + 1);
    let ema = series[0].value;
    const sm = [{ time: series[0].time, value: ema }];
    for (let i = 1; i < series.length; i++) {
      ema = k * series[i].value + (1 - k) * ema;
      sm.push({ time: series[i].time, value: ema });
    }
    series = sm;
  }

  return series;
}

// ESMD: First derivative of ESM (ΔESM) - rate of change in surprise momentum
// This shows whether surprise momentum is accelerating or decelerating
export function computeESMDerivative(esmSeries) {
  if (!esmSeries?.length) return [];
  const out = [];
  for (let i = 1; i < esmSeries.length; i++) {
    const prev = esmSeries[i - 1];
    const curr = esmSeries[i];
    if (!prev || !curr || !Number.isFinite(prev.value) || !Number.isFinite(curr.value)) continue;
    out.push({ time: curr.time, value: curr.value - prev.value });
  }
  return out;
}

// ED: simple drift in estimates (q/q % change in epsEstimated) - DEPRECATED, use ESMD instead
export function computeED(earnings, interval, lagDays) {
  if (!earnings?.length) return [];
  const pts = earnings
    .filter(e => e?.date && isFinite(e.epsEstimated))
    .map(e => {
      const d = new Date(e.fillingDate || e.acceptedDate || e.date); d.setDate(d.getDate() + lagDays);
      const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
      return { time: toEpoch(key), value: e.epsEstimated };
    })
    .sort((a, b) => a.time - b.time);
  const out = [];
  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1].value;
    if (!prev) continue;
    out.push({ time: pts[i].time, value: 100 * ((pts[i].value - prev) / Math.abs(prev)) });
  }
  return out;
}

/**
 * Next four quarterly EPS estimates at a given price-bar time (same sourcing everywhere).
 * Analyst next4 when “recent”, else forward epsEstimated rows, else trailing four from history.
 * @returns {number[] | null} four positive estimates, or null
 */
function nextFourQtrEpsEstimatesOrNull(sortedEarnings, currentTime, analystForecasts) {
  if (!sortedEarnings?.length) return null;
  let nextYearEpsEstimates = [];
  const mostRecentEarning = sortedEarnings[sortedEarnings.length - 1];
  if (analystForecasts?.eps) {
    if (mostRecentEarning && currentTime >= mostRecentEarning.time - 90 * 24 * 3600) {
      nextYearEpsEstimates = ntmEstimateRows(analystForecasts.eps)
        .filter(item => item.estimate != null && Number.isFinite(item.estimate) && item.estimate > 0)
        .map(item => item.estimate);
    }
  }
  if (nextYearEpsEstimates.length < 4) {
    for (let j = 0; j < sortedEarnings.length; j++) {
      const e = sortedEarnings[j];
      if (e.time > currentTime && e.epsEstimated != null && Number.isFinite(e.epsEstimated) && e.epsEstimated > 0) {
        nextYearEpsEstimates.push(e.epsEstimated);
        if (nextYearEpsEstimates.length >= 4) break;
      }
    }
    if (nextYearEpsEstimates.length < 4) {
      for (let j = sortedEarnings.length - 1; j >= 0; j--) {
        const e = sortedEarnings[j];
        if (e.epsEstimated != null && Number.isFinite(e.epsEstimated) && e.epsEstimated > 0) {
          nextYearEpsEstimates.push(e.epsEstimated);
          if (nextYearEpsEstimates.length >= 4) break;
        }
      }
    }
  }
  if (nextYearEpsEstimates.length < 4) return null;
  return nextYearEpsEstimates.slice(0, 4);
}

/**
 * Next four quarterly revenue estimates at a given price-bar time (mirrors EPS sourcing).
 * @returns {number[] | null} four positive estimates in dollars, or null
 */
function nextFourQtrRevenueEstimatesOrNull(sortedEarnings, currentTime, analystForecasts) {
  if (!sortedEarnings?.length) return null;
  let nextYearRevenueEstimates = [];
  const mostRecentEarning = sortedEarnings[sortedEarnings.length - 1];
  if (analystForecasts?.revenue) {
    if (mostRecentEarning && currentTime >= mostRecentEarning.time - 90 * 24 * 3600) {
      nextYearRevenueEstimates = ntmEstimateRows(analystForecasts.revenue)
        .filter((item) => item.estimate != null && Number.isFinite(item.estimate) && item.estimate > 0)
        .map((item) => item.estimate);
    }
  }
  if (nextYearRevenueEstimates.length < 4) {
    for (let j = 0; j < sortedEarnings.length; j++) {
      const e = sortedEarnings[j];
      if (e.time > currentTime && e.revenueEstimated != null && Number.isFinite(e.revenueEstimated) && e.revenueEstimated > 0) {
        nextYearRevenueEstimates.push(e.revenueEstimated);
        if (nextYearRevenueEstimates.length >= 4) break;
      }
    }
    if (nextYearRevenueEstimates.length < 4) {
      for (let j = sortedEarnings.length - 1; j >= 0; j--) {
        const e = sortedEarnings[j];
        if (e.revenueEstimated != null && Number.isFinite(e.revenueEstimated) && e.revenueEstimated > 0) {
          nextYearRevenueEstimates.push(e.revenueEstimated);
          if (nextYearRevenueEstimates.length >= 4) break;
        }
      }
    }
  }
  if (nextYearRevenueEstimates.length < 4) return null;
  return nextYearRevenueEstimates.slice(0, 4);
}

/** Average of next four quarterly EPS estimates (used by P/E ratio comparison forward leg). */
function avgNextFourQtrEpsEstimateOrNull(sortedEarnings, currentTime, analystForecasts) {
  const four = nextFourQtrEpsEstimatesOrNull(sortedEarnings, currentTime, analystForecasts);
  if (!four) return null;
  const avg = four.reduce((sum, eps) => sum + eps, 0) / 4;
  return Number.isFinite(avg) && avg > 0 ? avg : null;
}

/**
 * Forward P/E aligned with common sites (e.g. Yahoo): price ÷ NTM EPS ≈ sum of next four quarter estimates.
 * Using average quarterly EPS as the denominator makes the multiple ~4× too large vs Yahoo.
 */
/** Last four reported quarterly EPS values at or before a price-bar time. */
function lastFourQtrActualEpsOrNull(sortedEarnings, currentTime) {
  if (!sortedEarnings?.length) return null;
  const actual = [];
  for (let j = sortedEarnings.length - 1; j >= 0; j--) {
    const e = sortedEarnings[j];
    if (e.time > currentTime) continue;
    if (e.eps != null && Number.isFinite(e.eps) && e.eps > 0) {
      actual.push(e.eps);
      if (actual.length >= 4) break;
    }
  }
  if (actual.length < 4) return null;
  return actual.slice(0, 4);
}

export function computeForwardPE(earnings, px, interval, lagDays, analystForecasts = null) {
  if (!Array.isArray(earnings) || !earnings.length || !Array.isArray(px) || !px.length) return [];
  const sortedEarnings = [...earnings]
    .filter(e => e?.date)
    .map(e => {
      const fundDate = getFundDate(e);
      const d = new Date(fundDate);
      d.setDate(d.getDate() + (lagDays || 0));
      const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
      return { ...e, time: toEpoch(key), dateObj: fundDate };
    })
    .sort((a, b) => a.time - b.time);
  if (!sortedEarnings.length) return [];
  const out = [];
  for (let i = 0; i < px.length; i++) {
    const currentTime = px[i].time;
    const currentPrice = px[i].value;
    if (!Number.isFinite(currentPrice) || currentPrice <= 0) continue;
    const four = nextFourQtrEpsEstimatesOrNull(sortedEarnings, currentTime, analystForecasts);
    if (!four) continue;
    const ntmEps = four.reduce((sum, eps) => sum + eps, 0);
    if (!(ntmEps > 0)) continue;
    const forwardPE = currentPrice / ntmEps;
    if (Number.isFinite(forwardPE) && forwardPE > 0) out.push({ time: currentTime, value: forwardPE });
  }
  return out.sort((a, b) => a.time - b.time);
}

/**
 * TTM P/E: price ÷ sum of last four quarters' actual EPS (trailing twelve months).
 * Same bar-by-bar structure as Forward P/E, but uses reported EPS instead of estimates.
 */
export function computeTTMPE(earnings, px, interval, lagDays) {
  if (!Array.isArray(earnings) || !earnings.length || !Array.isArray(px) || !px.length) return [];
  const sortedEarnings = [...earnings]
    .filter(e => e?.date)
    .map(e => {
      const fundDate = getFundDate(e);
      const d = new Date(fundDate);
      d.setDate(d.getDate() + (lagDays || 0));
      const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
      return { ...e, time: toEpoch(key), dateObj: fundDate };
    })
    .sort((a, b) => a.time - b.time);
  if (sortedEarnings.length < 4) return [];
  const out = [];
  for (let i = 0; i < px.length; i++) {
    const currentTime = px[i].time;
    const currentPrice = px[i].value;
    if (!Number.isFinite(currentPrice) || currentPrice <= 0) continue;
    const four = lastFourQtrActualEpsOrNull(sortedEarnings, currentTime);
    if (!four) continue;
    const ttmEps = four.reduce((sum, eps) => sum + eps, 0);
    if (!(ttmEps > 0)) continue;
    const ttmPE = currentPrice / ttmEps;
    if (Number.isFinite(ttmPE) && ttmPE > 0) out.push({ time: currentTime, value: ttmPE });
  }
  return out.sort((a, b) => a.time - b.time);
}

const FGPZ_TRAIN_SEC = 7 * 365.25 * 24 * 3600;
const FGPZ_Z_LOOKBACK_SEC = 2 * 365 * 24 * 3600;
const FGPZ_MIN_TRAIN = 12;

/** OLS: ln(PE) ~ intercept + b1*gHist + b2*gFwd (%). */
function fitLnPeOnGrowthModel(rows) {
  if (!rows?.length || rows.length < FGPZ_MIN_TRAIN) return null;
  const n = rows.length;
  const X = rows.map((r) => [1, r.gHist, r.gFwd]);
  const Y = rows.map((r) => Math.log(r.fpe));
  const XtX = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      XtX[i][j] = X.reduce((s, row) => s + row[i] * row[j], 0);
    }
  }
  const XtY = [0, 0, 0];
  for (let i = 0; i < 3; i++) {
    XtY[i] = X.reduce((s, row, k) => s + row[i] * Y[k], 0);
  }
  const det = (m) =>
    m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1])
    - m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0])
    + m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0]);
  const d = det(XtX);
  if (Math.abs(d) < 1e-12) return null;
  const inv = [
    [(XtX[1][1] * XtX[2][2] - XtX[1][2] * XtX[2][1]) / d, (XtX[0][2] * XtX[2][1] - XtX[0][1] * XtX[2][2]) / d, (XtX[0][1] * XtX[1][2] - XtX[0][2] * XtX[1][1]) / d],
    [(XtX[1][2] * XtX[2][0] - XtX[1][0] * XtX[2][2]) / d, (XtX[0][0] * XtX[2][2] - XtX[0][2] * XtX[2][0]) / d, (XtX[0][2] * XtX[1][0] - XtX[0][0] * XtX[1][2]) / d],
    [(XtX[1][0] * XtX[2][1] - XtX[1][1] * XtX[2][0]) / d, (XtX[0][1] * XtX[2][0] - XtX[0][0] * XtX[2][1]) / d, (XtX[0][0] * XtX[1][1] - XtX[0][1] * XtX[1][0]) / d],
  ];
  const beta = [0, 0, 0];
  for (let i = 0; i < 3; i++) beta[i] = inv[i].reduce((s, v, j) => s + v * XtY[j], 0);
  return {
    predict(gHist, gFwd) {
      return beta[0] + beta[1] * gHist + beta[2] * gFwd;
    },
  };
}

/** Align 1y EPS CAGR, forward NTM EPS growth, forward P/E, and TTM P/E on each price bar. */
function buildGrowthMultipleAlignedRows(earnings, income, px, interval, lagDays, analystForecasts) {
  if (!px?.length) return [];
  const gHist = computeEPSCAGR(income, px, interval, lagDays, 1);
  const gFwd = computeEPSForwardCAGR(earnings, income, px, interval, lagDays, analystForecasts);
  const fpe = computeForwardPE(earnings, px, interval, lagDays, analystForecasts);
  const ttmPe = computeTTMPE(earnings, px, interval, lagDays);
  if (!gHist.length || !gFwd.length || !fpe.length || !ttmPe.length) return [];

  const rows = [];
  let kH = 0;
  let kF = 0;
  let kFp = 0;
  let kT = 0;
  for (const bar of px) {
    while (kH + 1 < gHist.length && gHist[kH + 1].time <= bar.time) kH++;
    while (kF + 1 < gFwd.length && gFwd[kF + 1].time <= bar.time) kF++;
    while (kFp + 1 < fpe.length && fpe[kFp + 1].time <= bar.time) kFp++;
    while (kT + 1 < ttmPe.length && ttmPe[kT + 1].time <= bar.time) kT++;
    const gh = gHist[kH]?.time <= bar.time ? gHist[kH]?.value : null;
    const gf = gFwd[kF]?.time <= bar.time ? gFwd[kF]?.value : null;
    const fp = fpe[kFp]?.time <= bar.time ? fpe[kFp]?.value : null;
    const tp = ttmPe[kT]?.time <= bar.time ? ttmPe[kT]?.value : null;
    if (!(Number.isFinite(gh) && Number.isFinite(gf) && Number.isFinite(fp) && Number.isFinite(tp))) continue;
    if (!(fp > 0 && tp > 0)) continue;
    rows.push({ time: bar.time, gHist: gh, gFwd: gf, fpe: fp, ttmPe: tp });
  }
  return rows;
}

/**
 * GAG — Growth Acceleration Gap (%).
 * (forward NTM EPS growth − trailing 1y EPS CAGR) − 100×(TTM P/E ÷ Forward P/E − 1).
 */
export function computeGrowthAccelerationGap(earnings, income, px, interval, lagDays, analystForecasts = null) {
  const aligned = buildGrowthMultipleAlignedRows(earnings, income, px, interval, lagDays, analystForecasts);
  const out = [];
  for (const r of aligned) {
    const gag = (r.gFwd - r.gHist) - 100 * (r.ttmPe / r.fpe - 1);
    if (Number.isFinite(gag)) out.push({ time: r.time, value: gag });
  }
  return out;
}

/**
 * FGPZ — Forward Growth Premium Z.
 * Z-score (2y) of ln(Forward P/E) residual vs stock-specific curve from trailing + forward EPS growth.
 */
export function computeForwardGrowthPremiumZ(earnings, income, px, interval, lagDays, analystForecasts = null) {
  const aligned = buildGrowthMultipleAlignedRows(earnings, income, px, interval, lagDays, analystForecasts);
  if (aligned.length < FGPZ_MIN_TRAIN + 1) return [];
  const residuals = [];
  for (let i = 0; i < aligned.length; i++) {
    const t = aligned[i].time;
    const cutoff = t - FGPZ_TRAIN_SEC;
    const train = aligned.filter((r, j) => j < i && r.time >= cutoff);
    if (train.length < FGPZ_MIN_TRAIN) continue;
    const model = fitLnPeOnGrowthModel(train);
    if (!model) continue;
    const pred = model.predict(aligned[i].gHist, aligned[i].gFwd);
    if (!Number.isFinite(pred)) continue;
    const res = Math.log(aligned[i].fpe) - pred;
    if (Number.isFinite(res)) residuals.push({ time: t, value: res });
  }
  return rollingZScoreTimeSeries(residuals, FGPZ_Z_LOOKBACK_SEC, (p) => p.value)
    .filter((p) => Number.isFinite(p?.value));
}

// P/E Ratio Comparison: (Historical P/E) / (Forward P/E)
// Historical P/E = average price last year / average actual EPS last year
// Forward P/E = today's price / average next year EPS estimates
// Ratio = Historical P/E / Forward P/E
// If ratio > 1: buying opportunity (historical P/E > forward P/E, stock relatively cheap vs expectations)
// If ratio < 1: overvalued (historical P/E < forward P/E, stock relatively expensive vs expectations)
// Entry signal: > 1.1, Exit signal: < 0.9
export function computePEPERatio(earnings, px, interval, lagDays, analystForecasts = null) {
  if (!Array.isArray(earnings) || !earnings.length || !Array.isArray(px) || !px.length) return [];
  
  // Sort earnings by date
  const sortedEarnings = [...earnings]
    .filter(e => e?.date)
    .map(e => {
      const fundDate = getFundDate(e);
      const d = new Date(fundDate);
      d.setDate(d.getDate() + (lagDays || 0));
      const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
      return {
        ...e,
        time: toEpoch(key),
        dateObj: fundDate
      };
    })
    .sort((a, b) => a.time - b.time);
  
  if (sortedEarnings.length < 8) return []; // Need at least 8 quarters (4 past + 4 future estimates)
  
  const ONE_YEAR_SEC = 365 * 24 * 3600;
  const out = [];
  
  // For each price point, calculate the ratio
  for (let i = 0; i < px.length; i++) {
    const currentTime = px[i].time;
    const currentPrice = px[i].value;
    if (!Number.isFinite(currentPrice) || currentPrice <= 0) continue;
    
    // 1) Get average of last year's price
    const oneYearAgo = currentTime - ONE_YEAR_SEC;
    const priceWindow = px.filter(p => p.time >= oneYearAgo && p.time <= currentTime);
    if (priceWindow.length === 0) continue;
    const avgPriceLastYear = priceWindow.reduce((sum, p) => sum + p.value, 0) / priceWindow.length;
    if (!Number.isFinite(avgPriceLastYear) || avgPriceLastYear <= 0) continue;
    
    // 2) Get average of past year's actual EPS (last 4 quarters with actual EPS that were reported before current time)
    const actualEpsValues = [];
    for (let j = sortedEarnings.length - 1; j >= 0; j--) {
      const e = sortedEarnings[j];
      
      // Only use earnings that were reported before or at current time
      if (e.time > currentTime) continue;
      
      // Use actual EPS if available
      if (e.eps != null && Number.isFinite(e.eps) && e.eps > 0) {
        actualEpsValues.push(e.eps);
        if (actualEpsValues.length >= 4) break; // Get last 4 quarters
      }
    }
    
    if (actualEpsValues.length < 4) continue; // Need at least 4 quarters of actual EPS
    const avgActualEpsLastYear = actualEpsValues.slice(0, 4).reduce((sum, eps) => sum + eps, 0) / 4;
    if (!Number.isFinite(avgActualEpsLastYear) || avgActualEpsLastYear <= 0) continue;

    const avgNextYearEpsEstimate = avgNextFourQtrEpsEstimateOrNull(sortedEarnings, currentTime, analystForecasts);
    if (avgNextYearEpsEstimate == null) continue;
    
    // 4) Calculate Historical P/E = avg price last year / avg actual EPS last year
    const historicalPE = avgPriceLastYear / avgActualEpsLastYear;
    if (!Number.isFinite(historicalPE) || historicalPE <= 0) continue;
    
    // 5) Calculate Forward P/E = today's price / avg next year EPS estimate
    const forwardPE = currentPrice / avgNextYearEpsEstimate;
    if (!Number.isFinite(forwardPE) || forwardPE <= 0) continue;
    
    // 6) Calculate ratio = Historical P/E / Forward P/E
    const ratio = historicalPE / forwardPE;
    if (Number.isFinite(ratio)) {
      out.push({ time: currentTime, value: ratio });
    }
  }
  
  return out.sort((a, b) => a.time - b.time);
}
// 1) MRVZ: Turnover-based MVRV-like Z. Use price / realized_price proxy, then Z-score it.
export function computeMRVZFromBars(dailyBars, interval, lagDays) {
  if (!Array.isArray(dailyBars) || dailyBars.length < 60) return [];
  const vwapCost = rollingVWAC(dailyBars, 252);
  const ratios = dailyBars.map((b, i) => {
    const rp = vwapCost[i];
    return Number.isFinite(b?.close) && Number.isFinite(rp) && rp > 0 ? b.close / rp : null;
  });
  const z = zScoreSeries(ratios, 252);
  const pts = [];
  for (let i = 0; i < dailyBars.length; i++) {
    if (!Number.isFinite(z[i]) || !dailyBars[i]?.date) continue;
    const d = new Date(dailyBars[i].date);
    if (lagDays) d.setDate(d.getDate() + lagDays);
    const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
    pts.push({ time: toEpoch(key), value: z[i] });
  }
  return pts.sort((a, b) => a.time - b.time);
}

// 2) VDDM: Value Days Destroyed Multiple. Improved version for better buy/sell signals.
// Key improvements:
// - Separates underwater (buy signal) from overvalued (sell signal)
// - Exponential decay for age (recent drawdowns matter more)
// - Enhanced volume weighting (emphasizes high-volume drawdowns)
// - Recovery momentum component (detects when price is recovering)
// - Percentile normalization for clearer thresholds
export function computeVDDM(input, opts = {}) {
  const window = Number.isFinite(opts.window) ? opts.window : 252; // VWAC + vol window
  const smooth = Number.isFinite(opts.smooth) ? opts.smooth : 63;  // smoothing window
  const normalize = opts.normalize ?? 'percentile';                // 'z' | 'percentile' | 'none'
  const epsilon = Number.isFinite(opts.epsilon) ? opts.epsilon : 1e-6;
  const ageDecay = Number.isFinite(opts.ageDecay) ? opts.ageDecay : 0.95; // exponential decay for age
  const volPower = Number.isFinite(opts.volPower) ? opts.volPower : 1.5;  // volume weighting power (>1 emphasizes high vol)
  const recoveryWindow = Number.isFinite(opts.recoveryWindow) ? opts.recoveryWindow : 21; // days to look back for recovery

  if (!Array.isArray(input) || input.length < Math.max(60, window)) return [];

  // Normalize input shape to arrays
  const isBars = input[0] && (Number.isFinite(input[0].close) || Number.isFinite(input[0].volume) || input[0].date);
  let times = [], closes = [], vols = [];

  if (isBars) {
    // daily bars: { date, close, volume }
    for (const b of input) {
      if (!b?.date) { times.push(null); closes.push(NaN); vols.push(NaN); continue; }
      const d = new Date(b.date);
      times.push(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 1000);
      closes.push(Number.isFinite(b?.close) ? b.close : NaN);
      vols.push(Number.isFinite(b?.volume) ? b.volume : 1);
    }
  } else {
    // price points: { time, value } with no volume
    for (const p of input) {
      times.push(p?.time ?? null);
      closes.push(Number.isFinite(p?.value) ? p.value : NaN);
      vols.push(1); // no volume provided
    }
  }

  const N = closes.length;

  // 1) Realized price proxy via VWAC over `window`
  const barsForVWAC = [];
  for (let i = 0; i < N; i++) barsForVWAC.push({ close: closes[i], volume: vols[i] });
  const realized = rollingVWAC(barsForVWAC, window);

  // 2) Enhanced volume weighting: use percentile rank of volume (emphasizes high-volume days)
  const volShare = [];
  {
    const Q = []; let sumV = 0;
    const volValues = [];
    for (let i = 0; i < N; i++) {
      const v = Number.isFinite(vols[i]) ? vols[i] : 0;
      Q.push(v); sumV += v;
      if (Q.length > window) sumV -= Q.shift();
      volValues.push(v);
      
      // Calculate volume percentile in rolling window
      if (Q.length >= 10) {
        const sorted = Q.slice().sort((a, b) => a - b);
        const rank = sorted.findIndex(x => x >= v);
        const volPercentile = sorted.length > 1 ? (rank / (sorted.length - 1)) : 0.5;
        // Power transform: high-volume days get more weight
        const volWeight = Math.pow(volPercentile, volPower);
        volShare.push(volWeight);
      } else {
        volShare.push(0.5); // neutral weight when insufficient data
      }
    }
  }

  // 3) Calculate price momentum for recovery detection
  const momentum = new Array(N).fill(null);
  for (let i = recoveryWindow; i < N; i++) {
    if (!Number.isFinite(closes[i]) || !Number.isFinite(closes[i - recoveryWindow])) continue;
    const pctChange = (closes[i] - closes[i - recoveryWindow]) / closes[i - recoveryWindow];
    momentum[i] = pctChange;
  }

  // 4) Per-bar value-days destroyed with improvements
  const vdd = new Array(N).fill(null);
  let prevDelta = null;
  let ageDays = 0;
  let effectiveAge = 0; // exponentially weighted age

  for (let i = 0; i < N; i++) {
    const c = closes[i], r = realized[i];
    if (!Number.isFinite(c) || !Number.isFinite(r) || r <= 0) { vdd[i] = null; continue; }

    const delta = (c - r) / r;                // relative P/L vs realized cost
    const sign  = Math.abs(delta) <= epsilon ? 0 : (delta > 0 ? 1 : -1);

    // Improved age calculation with exponential decay
    if (prevDelta === null) {
      ageDays = 1;
      effectiveAge = 1;
    } else {
      const prevSign = Math.abs(prevDelta) <= epsilon ? 0 : (prevDelta > 0 ? 1 : -1);
      if (sign === 0 || (prevSign !== 0 && sign !== prevSign)) {
        ageDays = 1;
        effectiveAge = 1;
      } else {
        ageDays = Math.min(ageDays + 1, window);
        // Exponential decay: recent days count more, older days decay
        effectiveAge = 1 + (effectiveAge * ageDecay);
      }
    }
    prevDelta = delta;

    // Recovery momentum adjustment: INCREASE signal when recovering from drawdown (better buy signal)
    // When underwater (delta < 0) and price is recovering (momentum > 0), this is a strong buy signal
    let recoveryFactor = 1.0;
    if (momentum[i] != null && delta < 0) {
      // If recovering from drawdown, boost the signal (up to 2x for strong recovery)
      recoveryFactor = 1.0 + Math.min(1.0, Math.max(0, momentum[i]) * 1.5);
    } else if (momentum[i] != null && delta > 0) {
      // If overvalued and still rising, reduce signal (sell signal)
      recoveryFactor = Math.max(0.5, 1.0 - Math.min(0.5, Math.max(0, momentum[i]) * 0.5));
    }

    // Key improvement: preserve sign for directional signals
    // Negative delta (underwater) = negative VDD (buy signal when very negative)
    // Positive delta (overvalued) = positive VDD (sell signal when very positive)
    const destroyed = delta * effectiveAge * (volShare[i] ?? 0.5) * recoveryFactor;
    vdd[i] = Number.isFinite(destroyed) ? destroyed : null;
  }

  // 4) Smooth (rolling sum) to get a momentum-ish signal
  const vddmRaw = rollingSum(vdd, smooth);

  // 5) Normalize - improved options
  let vddm = new Array(N).fill(null);
  if (normalize === 'z') {
    const zScores = rollingZScore(vddmRaw, Math.max(126, Math.min(window, 252)));
    for (let i = 0; i < N; i++) {
      vddm[i] = zScores[i];
    }
  } else if (normalize === 'percentile') {
    // Percentile normalization: better for buy/sell thresholds
    const LOOKBACK_YEARS = 3;
    const LOOKBACK_SEC = LOOKBACK_YEARS * 365 * 24 * 3600;
    
    // Build points array maintaining original indices
    const vddmPoints = [];
    for (let i = 0; i < N; i++) {
      if (times[i] != null && Number.isFinite(vddmRaw[i])) {
        vddmPoints.push({ time: times[i], value: vddmRaw[i], index: i });
      }
    }
    
    // rollingPercentile returns array of {time, value}, need to map back to original indices
    const percentileResults = rollingPercentile(vddmPoints, LOOKBACK_SEC, p => p.value);
    const percentileByTime = new Map(percentileResults.map(p => [p.time, p.value - 50]));
    
    // Map percentile values back to original array positions
    for (let i = 0; i < N; i++) {
      if (times[i] != null && percentileByTime.has(times[i])) {
        vddm[i] = percentileByTime.get(times[i]);
      } else if (times[i] != null && Number.isFinite(vddmRaw[i])) {
        // If percentile not available (insufficient history), use nearest available percentile
        // or fall back to raw value scaled
        let nearestPercentile = null;
        let minTimeDiff = Infinity;
        for (const [time, pct] of percentileByTime.entries()) {
          const diff = Math.abs(times[i] - time);
          if (diff < minTimeDiff && diff < 7 * 24 * 3600) { // within 7 days
            minTimeDiff = diff;
            nearestPercentile = pct;
          }
        }
        vddm[i] = nearestPercentile;
      }
    }
  } else {
    // No normalization - use raw values
    for (let i = 0; i < N; i++) {
      vddm[i] = vddmRaw[i];
    }
  }

  // Pack for Lightweight Charts - output all valid points
  const out = [];
  for (let i = 0; i < N; i++) {
    const t = times[i], v = vddm[i];
    if (t != null && Number.isFinite(v)) {
      out.push({ time: t, value: v });
    }
  }
  return out;
}

// SRM: Simple Risk Metric = percentile of rolling max drawdown depth
export function computeSRM(px, opts = {}) {
  if (!Array.isArray(px) || px.length < 30) return [];
  const windowDays    = Number.isFinite(opts.windowDays)    ? opts.windowDays    : 126; // ~6 months
  const lookbackYears = Number.isFinite(opts.lookbackYears) ? opts.lookbackYears : 5;   // percentile window
  const LOOKBACK_SEC  = lookbackYears * 365 * 24 * 3600;

  // 1) Rolling max drawdown over the last `windowDays`
  //    ddDepth = max(0, 1 - price / rollingPeak)  ==> 0..1
  const vals = px.map(p => p.value);
  const times = px.map(p => p.time);
  const ddPoints = [];

  for (let i = 0; i < vals.length; i++) {
    const start = Math.max(0, i - windowDays + 1);
    let peak = -Infinity;
    for (let k = start; k <= i; k++) {
      const v = vals[k];
      if (Number.isFinite(v) && v > peak) peak = v;
    }
    if (!Number.isFinite(peak) || peak <= 0 || !Number.isFinite(vals[i])) {
      ddPoints.push({ time: times[i], value: null });
      continue;
    }
    const ddDepth = Math.max(0, 1 - vals[i] / peak); // 0..1
    ddPoints.push({ time: times[i], value: ddDepth * 100 }); // percent
  }

  // 2) Rolling percentile rank (0..100) of drawdown severity over ~5 years
  const srm = rollingPercentile(
    ddPoints.filter(p => p && p.time != null),
    LOOKBACK_SEC,
    p => p.value
  ).filter(p => Number.isFinite(p?.value));

  return srm;
}


// Realized price proxy: rolling volume-weighted average cost (VWAC) over N trading days.
export function computeRealizedPriceFromBars(dailyBars, window, interval, lagDays) {
  if (!Array.isArray(dailyBars) || dailyBars.length < Math.min(60, window)) return [];
  const vwapCost = rollingVWAC(dailyBars, window);
  const pts = [];
  for (let i = 0; i < dailyBars.length; i++) {
    const b = dailyBars[i];
    const rp = vwapCost[i];
    if (!b?.date || !Number.isFinite(rp) || rp <= 0) continue;
    const d = new Date(b.date);
    if (lagDays) d.setDate(d.getDate() + lagDays);
    const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
    pts.push({ time: toEpoch(key), value: rp });
  }
  return pts.sort((a, b) => a.time - b.time);
}

export const REALIZED_PRICE_UP_COLOR = "#22c55e";
export const REALIZED_PRICE_DOWN_COLOR = "#ef4444";

/** Split a time series into runs colored by sign of the first derivative (flat → down/red). */
export function splitSeriesBySlope(points) {
  const pts = (points || [])
    .filter((p) => p && Number.isFinite(p.time) && Number.isFinite(p.value))
    .sort((a, b) => a.time - b.time);
  if (pts.length < 2) return [];

  const segments = [];
  let startIdx = 0;

  for (let i = 1; i < pts.length; i++) {
    const legRising = pts[i].value > pts[i - 1].value;
    const atEnd = i === pts.length - 1;
    const nextLegRising = atEnd ? legRising : pts[i + 1].value > pts[i].value;

    if (atEnd || legRising !== nextLegRising) {
      segments.push({
        rising: legRising,
        points: pts.slice(startIdx, i + 1),
      });
      startIdx = i;
    }
  }

  return segments;
}

/** Highcharts `[ms, value][]` → slope-colored segment series. */
export function splitHighchartsLineBySlope(lineData) {
  const points = (lineData || [])
    .filter((row) => Array.isArray(row) && Number.isFinite(row[0]) && Number.isFinite(row[1]))
    .map(([t, v]) => ({ time: t / 1000, value: v }));
  return splitSeriesBySlope(points).map((seg) => ({
    rising: seg.rising,
    color: seg.rising ? REALIZED_PRICE_UP_COLOR : REALIZED_PRICE_DOWN_COLOR,
    data: seg.points.map((p) => [p.time * 1000, p.value]),
  }));
}

/** Period-over-period % change — first derivative of realized price (and similar series). */
export function computeRealizedPriceDerivative(points) {
  const pts = (points || [])
    .filter((p) => p && Number.isFinite(p.time) && Number.isFinite(p.value))
    .sort((a, b) => a.time - b.time);
  const out = [];
  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1].value;
    if (!(prev > 0)) continue;
    const pct = ((pts[i].value - prev) / prev) * 100;
    if (!Number.isFinite(pct)) continue;
    out.push({ time: pts[i].time, value: pct });
  }
  return out;
}

/** Split into runs where value > 0 (positive) vs <= 0 (negative/flat). */
export function splitSeriesByValueSign(points) {
  const pts = (points || [])
    .filter((p) => p && Number.isFinite(p.time) && Number.isFinite(p.value))
    .sort((a, b) => a.time - b.time);
  if (pts.length < 2) return [];

  const segments = [];
  let startIdx = 0;

  for (let i = 0; i < pts.length; i++) {
    const positive = pts[i].value > 0;
    const atEnd = i === pts.length - 1;
    const nextPositive = atEnd ? positive : pts[i + 1].value > 0;

    if (atEnd || positive !== nextPositive) {
      segments.push({
        positive,
        points: pts.slice(startIdx, i + 1),
      });
      startIdx = i;
    }
  }

  return segments;
}

/** Highcharts `[ms, value][]` colored by sign (for derivative pane). */
export function splitHighchartsLineByValueSign(lineData) {
  const points = (lineData || [])
    .filter((row) => Array.isArray(row) && Number.isFinite(row[0]) && Number.isFinite(row[1]))
    .map(([t, v]) => ({ time: t / 1000, value: v }));
  return splitSeriesByValueSign(points).map((seg) => ({
    positive: seg.positive,
    color: seg.positive ? REALIZED_PRICE_UP_COLOR : REALIZED_PRICE_DOWN_COLOR,
    data: seg.points.map((p) => [p.time * 1000, p.value]),
  }));
}

// 3) NUPL proxy for equities: (price - realized_price)/price using the same 252d VWAC cost.
export function computeNUPLProxyFromBars(dailyBars, interval, lagDays) {
  if (!Array.isArray(dailyBars) || dailyBars.length < 60) return [];
  const vwapCost = rollingVWAC(dailyBars, 252);
  const pts = [];
  for (let i = 0; i < dailyBars.length; i++) {
    const b = dailyBars[i], rp = vwapCost[i];
    if (!b?.date || !Number.isFinite(b.close) || !Number.isFinite(rp) || b.close <= 0) continue;
    const nupl = (b.close - rp) / b.close; // negative when underwater, positive in profit
    const d = new Date(b.date);
    if (lagDays) d.setDate(d.getDate() + lagDays);
    const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
    pts.push({ time: toEpoch(key), value: 100 * nupl }); // percent
  }
  return pts.sort((a, b) => a.time - b.time);
}

// Helper: Compute realized volatility (annualized) from price series
function computeRealizedVolatility(px, window = 20) {
  if (!Array.isArray(px) || px.length < window + 1) return [];
  const out = [];
  for (let i = window; i < px.length; i++) {
    const returns = [];
    for (let j = i - window + 1; j <= i; j++) {
      if (j > 0 && px[j]?.value && px[j-1]?.value && px[j-1].value > 0) {
        const ret = Math.log(px[j].value / px[j-1].value);
        if (Number.isFinite(ret)) returns.push(ret);
      }
    }
    if (returns.length < window / 2) {
      out.push({ time: px[i].time, value: null });
      continue;
    }
    const meanRet = returns.reduce((s, x) => s + x, 0) / returns.length;
    const variance = returns.reduce((s, x) => s + (x - meanRet) ** 2, 0) / (returns.length - 1);
    const dailyVol = Math.sqrt(variance);
    const annualizedVol = dailyVol * Math.sqrt(252); // annualize
    out.push({ time: px[i].time, value: Number.isFinite(annualizedVol) ? annualizedVol : null });
  }
  // Pad beginning with nulls
  const padded = new Array(window).fill(null).map((_, i) => ({ time: px[i]?.time, value: null }));
  return [...padded, ...out];
}

/**
 * Composite Risk 0–100: Improved version with all enhancements
 * Combines valuation (VMZ), momentum (VMO), mean-reversion Z, drawdown (SRM), and volatility
 * 
 * ENTRY/EXIT RECOMMENDATIONS:
 * ============================
 * 
 * ENTRY (Buying Opportunities - Low Risk):
 * -----------------------------------------
 * Risk < 25:  Excellent entry zone - stock is undervalued, low momentum risk, not stretched
 * Risk < 30:  Good entry zone - favorable risk/reward (current default)
 * Risk < 35:  Moderate entry zone - acceptable risk for value investors
 * 
 * EXIT (Selling Opportunities - High Risk):
 * ------------------------------------------
 * Risk ≥ 50:  Consider taking profits - risk is elevated
 * Risk ≥ 60:  Strong sell signal - stock is getting expensive and stretched
 * Risk ≥ 70:  Aggressive sell signal - high risk of pullback (current default)
 * Risk ≥ 80:  Extreme risk - consider exiting or reducing position significantly
 * 
 * STRATEGY RECOMMENDATIONS:
 * ------------------------
 * 
 * 1. CONSERVATIVE (Long-term value investors):
 *    Entry: Risk < 25
 *    Exit: Risk ≥ 60
 *    Rationale: Wait for deep value, exit when risk becomes elevated
 * 
 * 2. MODERATE (Balanced approach):
 *    Entry: Risk < 30 (DCA while below)
 *    Exit: Risk ≥ 70
 *    Rationale: Current default strategy, good risk/reward balance
 * 
 * 3. AGGRESSIVE (Momentum/value blend):
 *    Entry: Risk < 35
 *    Exit: Risk ≥ 50 (take profits early)
 *    Rationale: Enter earlier, exit earlier to capture swings
 * 
 * 4. CROSSOVER STRATEGY:
 *    Entry: Risk crosses below 30 from above (momentum shift to low risk)
 *    Exit: Risk crosses above 70 from below (momentum shift to high risk)
 *    Rationale: Capture momentum shifts, fewer trades but higher conviction
 * 
 * NOTES:
 * ------
 * - Uses hybrid normalization (percentile + absolute z-scores) to catch extremes better
 * - 5-year lookback for percentile normalization (more stable than 3-year)
 * - Signed z-score: high price = high risk, low price = low risk (better price correlation)
 * - EMA smoothing (10-period default) reduces noise and improves signal quality
 * - Reduced volatility adjustment impact (gentler, less noisy)
 * - Increased VMZ weight (40%) for better correlation with price levels
 * - Multi-timeframe analysis provides more robust signals
 * - Drawdown component (SRM) adds stress detection
 * - Always combine with other indicators (SRM, MRVZ, VDDM) for confirmation
 * - Consider position sizing: lower risk = larger position, higher risk = smaller position
 * 
 * IMPROVEMENTS FOR NOISE REDUCTION:
 * ---------------------------------
 * - Smoothing: Final risk score is smoothed with EMA to reduce day-to-day noise
 * - Longer lookback: 5-year window makes percentile ranks more stable
 * - Hybrid normalization: Combines adaptive (percentile) with absolute (z-score) thresholds
 * - Signed z-score: Properly maps high prices to high risk (not just "stretched")
 * - Reduced vol adjustment: Less aggressive volatility multiplier reduces false signals
 */
export function computeRiskComposite({ px, vmz, vmo, srm = [], opts = {} }) {
  if (!Array.isArray(px) || px.length < 30) return [];
  
  const {
    // Rolling window for percentile normalization (in years) - longer for stability
    lookbackYears = 5,  // Increased from 3 to 5 for better stability
    // Multi-timeframe mean-reversion windows (days)
    mrWindows = [50, 126, 252],
    // Maximum time gap for alignment (seconds, ~90 days default)
    maxGapSec = 60 * 60 * 24 * 90,
    // Volatility window for realized vol calculation (days)
    volWindow = 20,
    // Volatility lookback for median calculation (years)
    volLookbackYears = 2,
    // Component weights (must sum to 1.0)
    weights = {
      vmz: 0.40,    // Increased valuation weight for better price correlation
      vmo: 0.25,    // Reduced momentum weight
      zScore: 0.25, // Mean-reversion z-score
      drawdown: 0.10, // Drawdown component (SRM)
    },
    // Enable volatility adjustment (reduced impact)
    useVolAdjustment = true,
    // Smoothing window for final risk score (EMA period)
    smoothWindow = 10,  // New: smooth the final output to reduce noise
    // Use hybrid normalization (percentile + absolute thresholds)
    useHybridNorm = true,  // New: combine percentile with absolute z-scores
  } = opts;

  // Normalize weights to sum to 1.0
  let normalizedWeights = { ...weights };
  const weightSum = normalizedWeights.vmz + normalizedWeights.vmo + normalizedWeights.zScore + normalizedWeights.drawdown;
  if (Math.abs(weightSum - 1.0) > 0.01 && weightSum > 0) {
    const scale = 1.0 / weightSum;
    normalizedWeights = {
      vmz: normalizedWeights.vmz * scale,
      vmo: normalizedWeights.vmo * scale,
      zScore: normalizedWeights.zScore * scale,
      drawdown: normalizedWeights.drawdown * scale,
    };
  }

  const LOOKBACK_SEC = lookbackYears * 365 * 24 * 3600;
  const VOL_LOOKBACK_SEC = volLookbackYears * 365 * 24 * 3600;

  // 1) Multi-timeframe mean-reversion Z-scores
  const zScores = [];
  for (const window of mrWindows) {
    const bands = rollingBands(px, window, 5);
  const z = px.map((p, i) => {
      const sd = bands.sd[i]?.value || 0;
      const mu = bands.mean[i]?.value || 0;
    return sd > 0 ? (p.value - mu) / sd : 0;
  });
    zScores.push(z);
  }
  // Average z-scores across timeframes (weighted by inverse window size for shorter-term emphasis)
  const weightsZ = mrWindows.map(w => 1 / w);
  const sumWeightsZ = weightsZ.reduce((s, w) => s + w, 0);
  const zCombined = px.map((_, i) => {
    let sum = 0;
    for (let j = 0; j < zScores.length; j++) {
      const zVal = zScores[j][i] ?? 0;
      sum += zVal * (weightsZ[j] / sumWeightsZ);
    }
    return sum;
  });
  // Use SIGNED z-score: positive = price above mean (high risk), negative = price below mean (low risk)
  // We'll normalize this separately to map to 0-1 range where high z = high risk
  const zSigned = zCombined;

  // 2) Realized volatility for regime adjustment
  const volSeries = useVolAdjustment ? computeRealizedVolatility(px, volWindow) : [];
  const volValues = volSeries.filter(p => Number.isFinite(p?.value)).map(p => p.value);

  // 3) Build series for percentile normalization
  const vmzPoints = (vmz || []).filter(p => p && Number.isFinite(p.value));
  const vmoPoints = (vmo || []).filter(p => p && Number.isFinite(p.value));
  const srmPoints = (srm || []).filter(p => p && Number.isFinite(p.value));
  
  // Pre-build VMO absolute series and Z-signed points for efficiency
  const vmoAbsPoints = vmoPoints.map(p => ({ time: p.time, value: Math.abs(p.value) }));
  // For z-score, we want to normalize signed values: positive z (high price) = high risk
  // Map z-score to 0-1: negative z -> low risk (0-0.5), positive z -> high risk (0.5-1)
  const zNormalized = zSigned.map(z => {
    // Use tanh to map z-score to 0-1 range: negative z -> near 0, positive z -> near 1
    // tanh maps -inf to -1, 0 to 0, +inf to +1, so we shift: (tanh(z/2) + 1) / 2
    return (Math.tanh(z / 2) + 1) / 2; // Maps to 0-1 where 0 = very low price, 1 = very high price
  });
  const zPoints = px.map((p, i) => ({ time: p.time, value: zNormalized[i] ?? 0.5 }));

  // 4) Hybrid normalization: combines percentile rank with absolute z-score thresholds
  // This prevents the indicator from being too adaptive and missing extremes
  function hybridNormalize(points, currentTime, currentValue, lookbackSec, rawValue, useHybrid) {
    if (!Number.isFinite(currentValue)) return 0.5;
    
    // Percentile rank component
    const window = points.filter(p => 
      p.time >= currentTime - lookbackSec && 
      p.time <= currentTime &&
      Number.isFinite(p.value)
    );
    if (window.length < 8) return 0.5;
    
    const values = window.map(p => p.value).sort((a, b) => a - b);
    const n = values.length;
    
    let percentile = 0.5;
    if (currentValue <= values[0]) {
      percentile = 0;
    } else if (currentValue >= values[n - 1]) {
      percentile = 1;
    } else {
      let lo = 0, hi = n - 1;
      while (lo <= hi) {
        const m = (lo + hi) >> 1;
        if (values[m] < currentValue) lo = m + 1;
        else hi = m - 1;
      }
      percentile = lo / (n - 1);
    }
    
    // If not using hybrid, return percentile only
    if (!useHybrid || !Number.isFinite(rawValue)) return percentile;
    
    // Absolute z-score component (for VMZ, VMO, Z-score)
    // Convert raw value to z-score relative to historical distribution
    const mean = values.reduce((s, x) => s + x, 0) / n;
    const variance = values.reduce((s, x) => s + (x - mean) ** 2, 0) / (n - 1);
    const stdDev = Math.sqrt(variance);
    
    if (stdDev > 0) {
      const zScore = (rawValue - mean) / stdDev;
      // Convert z-score to 0-1 range using tanh (smooth, bounded)
      // Positive z-scores (high values) map to high risk
      const absZ = Math.abs(zScore);
      const zNorm = Math.min(1, Math.max(0, Math.tanh(absZ / 2))); // tanh maps to 0-1
      
      // Blend: 70% percentile (adaptive), 30% absolute z-score (catches extremes)
      return 0.7 * percentile + 0.3 * zNorm;
    }
    
    return percentile;
  }
  
  // Alias for backward compatibility
  const percentileRank = (points, currentTime, currentValue, lookbackSec) => 
    hybridNormalize(points, currentTime, currentValue, lookbackSec, currentValue, false);

  // 5) Compute normalized components for each price point
  const out = [];
  for (let i = 0; i < px.length; i++) {
    const p = px[i];
    const t = p.time;

    // Get nearest points with gap checking
    const vmzPt = nearestPoint(vmzPoints, t, maxGapSec);
    const vmoPt = nearestPoint(vmoPoints, t, maxGapSec);
    const srmPt = nearestPoint(srmPoints, t, maxGapSec);
    const volPt = useVolAdjustment ? nearestPoint(volSeries, t, maxGapSec) : null;

    // Normalize using hybrid approach (percentile + absolute thresholds)
    const vmzN = vmzPt ? hybridNormalize(vmzPoints, t, vmzPt.value, LOOKBACK_SEC, vmzPt.value, useHybridNorm) : 0.5;
    // Use absolute VMO to capture both positive and negative momentum risk
    const vmoAbs = vmoPt ? Math.abs(vmoPt.value) : 0;
    const vmoN = vmoPt ? hybridNormalize(vmoAbsPoints, t, vmoAbs, LOOKBACK_SEC, vmoAbs, useHybridNorm) : 0.5;
    
    // Z-score normalization: already normalized to 0-1 (high price = high risk, low price = low risk)
    // Apply percentile smoothing to reduce noise while preserving direction
    const zVal = zNormalized[i] ?? 0.5;
    // Use percentile rank for smoothing, but preserve the direction (high z = high risk)
    const zN = percentileRank(zPoints, t, zVal, LOOKBACK_SEC);

    // Drawdown component (SRM already in percentile form, but normalize to 0..1)
    const drawdownN = srmPt ? (srmPt.value / 100) : 0.5;

    // Volatility adjustment factor
    let volAdj = 1.0;
    if (useVolAdjustment && volPt && volValues.length > 0) {
      // Calculate median volatility over lookback period
      const volWindow = volSeries.filter(v => 
        v.time >= t - VOL_LOOKBACK_SEC && 
        v.time <= t &&
        Number.isFinite(v.value)
      ).map(v => v.value);
      
      if (volWindow.length > 10) {
        const sorted = volWindow.slice().sort((a, b) => a - b);
        const medianVol = sorted.length % 2 === 0
          ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
          : sorted[Math.floor(sorted.length / 2)];
        
        if (medianVol > 0 && Number.isFinite(volPt.value)) {
          // Scale risk by volatility: higher vol = higher risk multiplier
          // Reduced impact: cap at 1.2x instead of 1.5x, and use square root to reduce sensitivity
          const volRatio = volPt.value / medianVol;
          const adjustedRatio = 1 + (Math.sqrt(volRatio) - 1) * 0.3; // Much gentler adjustment
          volAdj = Math.min(1.2, Math.max(0.9, adjustedRatio));
        }
      }
    }

    // Weighted combination
    const risk01 = 
      normalizedWeights.vmz * vmzN +
      normalizedWeights.vmo * vmoN +
      normalizedWeights.zScore * zN +
      normalizedWeights.drawdown * drawdownN;

    // Apply volatility adjustment
    const finalRisk = Math.min(1.0, Math.max(0.0, risk01 * volAdj));
    
    out.push({ time: t, value: finalRisk }); // Store raw value for smoothing
  }

  // Apply smoothing to reduce noise (EMA)
  if (smoothWindow > 1 && out.length > 0) {
    const smoothed = [];
    const k = 2 / (smoothWindow + 1);
    let ema = out[0].value;
    smoothed.push({ time: out[0].time, value: Math.round(100 * ema) });
    
    for (let i = 1; i < out.length; i++) {
      ema = k * out[i].value + (1 - k) * ema;
      smoothed.push({ time: out[i].time, value: Math.round(100 * ema) });
    }
    return smoothed;
  }

  // Round to integers if no smoothing
  return out.map(p => ({ time: p.time, value: Math.round(100 * p.value) }));
}

/** Linear regression with 95% prediction interval. pairs = [[x,y], ...]. Returns predict(x) => { point, low, high } or null. */
function linearRegressionWithPI(pairs) {
  if (!pairs || pairs.length < 3) return null;
  const n = pairs.length;
  const sumX = pairs.reduce((s, p) => s + p[0], 0);
  const sumY = pairs.reduce((s, p) => s + p[1], 0);
  const sumXY = pairs.reduce((s, p) => s + p[0] * p[1], 0);
  const sumX2 = pairs.reduce((s, p) => s + p[0] * p[0], 0);
  const meanX = sumX / n;
  const meanY = sumY / n;
  const ssXX = sumX2 - n * meanX * meanX;
  const ssXY = sumXY - n * meanX * meanY;
  if (ssXX === 0) return null;
  const slope = ssXY / ssXX;
  const intercept = meanY - slope * meanX;
  const residuals = pairs.map(p => p[1] - (intercept + slope * p[0]));
  const mse = residuals.reduce((s, r) => s + r * r, 0) / (n - 2);
  const se = Math.sqrt(mse);
  const t95 = 1.96;
  return (x) => {
    const pred = intercept + slope * x;
    const xDiff = x - meanX;
    const sePred = se * Math.sqrt(1 + 1 / n + (xDiff * xDiff) / ssXX);
    return {
      point: pred,
      low: pred - t95 * sePred,
      high: pred + t95 * sePred,
    };
  };
}

export function computeRevenueValuationIndicator({ earnings, px, interval, lagDays, analystForecasts }) {
  let src = [];
  if (Array.isArray(earnings)) {
    src = earnings;
  } else if (earnings && typeof earnings === "object" && Array.isArray(earnings.historical)) {
    src = earnings.historical;
  } else if (earnings && typeof earnings === "object" && Array.isArray(earnings.earnings)) {
    src = earnings.earnings;
  }
  if (!px?.length) return { series: [], bounds: [] };
  const minPxTime = px[0]?.time ?? 0;
  const sorted = [...src]
    .filter(e => (e.revenue != null && isFinite(e.revenue)) && (e.fillingDate || e.acceptedDate || e.date))
    .map(e => {
      const d = new Date(e.fillingDate || e.acceptedDate || e.date);
      d.setDate(d.getDate() + (lagDays || 0));
      const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
      const [Y, M, D] = key.split("-").map(Number);
      const time = Date.UTC(Y, M - 1, D) / 1000;
      const rev = (e.revenue || 0) / 1e9;
      const revEst = e.revenueEstimated != null && isFinite(e.revenueEstimated) ? e.revenueEstimated / 1e9 : null;
      return { time, revenue: rev, revenueEstimated: revEst };
    })
    .sort((a, b) => a.time - b.time);
  const MIN_QUARTERS = 8; // At least 2 years (8 quarters) of actual revenue for model
  const FIVE_YEAR_SEC = 5 * 365.25 * 24 * 3600; // Max 5 years of data for model
  const series = [];
  const bounds = [];
  // Historical expanding window: for each quarter i, model on actuals [0..i-1] within last 5y, predict for quarter i
  for (let i = MIN_QUARTERS; i < sorted.length; i++) {
    const revForecast = sorted[i].revenueEstimated;
    if (revForecast == null || !isFinite(revForecast) || revForecast <= 0) continue;
    const cutoffTime = sorted[i].time - FIVE_YEAR_SEC;
    const actualRevs = sorted.slice(0, i).filter(r => r.time >= cutoffTime); // Last 5y only
    const pairs = actualRevs
      .map(r => {
        const price = findNearestPriceValue(r.time, px);
        return price != null && price > 0 ? [r.revenue, price] : null;
      })
      .filter(Boolean);
    if (pairs.length < MIN_QUARTERS) continue; // Require at least 8 quarters
    const model = linearRegressionWithPI(pairs);
    if (!model) continue;
    const revB = revForecast;
    const { point: pred, low, high } = model(revB);
    if (pred <= 0) continue;
    const tStart = i > 0 ? sorted[i - 1].time : minPxTime;
    const tEnd = sorted[i].time;
    for (const bar of px) {
      if (bar.time < tStart || bar.time > tEnd) continue;
      const ratio = bar.value / pred;
      series.push({ time: bar.time, value: ratio });
      bounds.push({ time: bar.time, low: low / pred, high: high / pred });
    }
  }
  // Forward-looking: model on last 5y of actual quarters, use analyst next4 for next quarter revenue forecasts
  const lastQuarterTime = sorted.length > 0 ? sorted[sorted.length - 1].time : 0;
  const forwardCutoff = lastQuarterTime - FIVE_YEAR_SEC;
  const allActualPairs = sorted
    .filter(r => r.time >= forwardCutoff)
    .map(r => {
      const price = findNearestPriceValue(r.time, px);
      return price != null && price > 0 ? [r.revenue, price] : null;
    })
    .filter(Boolean);
  if (allActualPairs.length >= MIN_QUARTERS && forwardEstimateRows(analystForecasts?.revenue).length) {
    const model = linearRegressionWithPI(allActualPairs);
    if (model) {
      let tStart = sorted.length > 0 ? sorted[sorted.length - 1].time : minPxTime;
      for (const item of forwardEstimateRows(analystForecasts.revenue)) {
        const dateStr = item.fiscalDateEnding || item.date;
        if (!dateStr || item.estimate == null) continue;
        const revB = item.estimate / 1e9;
        if (revB <= 0) continue;
        const { point: pred, low, high } = model(revB);
        if (pred <= 0) continue;
        const [Y, M, D] = dateStr.split("-").map(Number);
        const tEnd = Date.UTC(Y, M - 1, D) / 1000;
        for (const bar of px) {
          if (bar.time <= tStart || bar.time > tEnd) continue;
          const ratio = bar.value / pred;
          series.push({ time: bar.time, value: ratio });
          bounds.push({ time: bar.time, low: low / pred, high: high / pred });
        }
        tStart = tEnd;
      }
    }
  }
  const dedup = (arr) => {
    const seen = new Map();
    for (const p of arr) {
      if (!seen.has(p.time)) seen.set(p.time, p);
    }
    return Array.from(seen.values()).sort((a, b) => a.time - b.time);
  };
  return {
    series: dedup(series),
    bounds: dedup(bounds),
  };
}

/** EPS-based fair value: same logic as revenue valuation but Price ~ EPS. Value = current price / predicted price from EPS. */
export function computeEPSValuationIndicator({ earnings, px, interval, lagDays, analystForecasts }) {
  let src = [];
  if (Array.isArray(earnings)) {
    src = earnings;
  } else if (earnings && typeof earnings === "object" && Array.isArray(earnings.historical)) {
    src = earnings.historical;
  } else if (earnings && typeof earnings === "object" && Array.isArray(earnings.earnings)) {
    src = earnings.earnings;
  }
  if (!px?.length) return { series: [], bounds: [] };
  const minPxTime = px[0]?.time ?? 0;
  const sorted = [...src]
    .filter(e => (e.eps != null && isFinite(e.eps)) && (e.fillingDate || e.acceptedDate || e.date))
    .map(e => {
      const d = new Date(e.fillingDate || e.acceptedDate || e.date);
      d.setDate(d.getDate() + (lagDays || 0));
      const key = alignDateToInterval(d.toISOString().split("T")[0], interval);
      const [Y, M, D] = key.split("-").map(Number);
      const time = Date.UTC(Y, M - 1, D) / 1000;
      const epsEst = e.epsEstimated != null && isFinite(e.epsEstimated) ? e.epsEstimated : null;
      return { time, eps: e.eps, epsEstimated: epsEst };
    })
    .sort((a, b) => a.time - b.time);
  const MIN_QUARTERS = 8;
  const FIVE_YEAR_SEC = 5 * 365.25 * 24 * 3600;
  const series = [];
  const bounds = [];
  for (let i = MIN_QUARTERS; i < sorted.length; i++) {
    const epsForecast = sorted[i].epsEstimated;
    if (epsForecast == null || !isFinite(epsForecast) || epsForecast <= 0) continue;
    const cutoffTime = sorted[i].time - FIVE_YEAR_SEC;
    const actualRows = sorted.slice(0, i).filter(r => r.time >= cutoffTime);
    const pairs = actualRows
      .map(r => {
        const price = findNearestPriceValue(r.time, px);
        return price != null && price > 0 ? [r.eps, price] : null;
      })
      .filter(Boolean);
    if (pairs.length < MIN_QUARTERS) continue;
    const model = linearRegressionWithPI(pairs);
    if (!model) continue;
    const { point: pred, low, high } = model(epsForecast);
    if (pred <= 0) continue;
    const tStart = i > 0 ? sorted[i - 1].time : minPxTime;
    const tEnd = sorted[i].time;
    for (const bar of px) {
      if (bar.time < tStart || bar.time > tEnd) continue;
      const ratio = bar.value / pred;
      series.push({ time: bar.time, value: ratio });
      bounds.push({ time: bar.time, low: low / pred, high: high / pred });
    }
  }
  const lastQuarterTime = sorted.length > 0 ? sorted[sorted.length - 1].time : 0;
  const forwardCutoff = lastQuarterTime - FIVE_YEAR_SEC;
  const allActualPairs = sorted
    .filter(r => r.time >= forwardCutoff)
    .map(r => {
      const price = findNearestPriceValue(r.time, px);
      return price != null && price > 0 ? [r.eps, price] : null;
    })
    .filter(Boolean);
  if (allActualPairs.length >= MIN_QUARTERS && forwardEstimateRows(analystForecasts?.eps).length) {
    const model = linearRegressionWithPI(allActualPairs);
    if (model) {
      let tStart = sorted.length > 0 ? sorted[sorted.length - 1].time : minPxTime;
      for (const item of forwardEstimateRows(analystForecasts.eps)) {
        const dateStr = item.fiscalDateEnding || item.date;
        if (!dateStr || item.estimate == null) continue;
        const epsF = item.estimate;
        if (!isFinite(epsF) || epsF <= 0) continue;
        const { point: pred, low, high } = model(epsF);
        if (pred <= 0) continue;
        const [Y, M, D] = dateStr.split("-").map(Number);
        const tEnd = Date.UTC(Y, M - 1, D) / 1000;
        for (const bar of px) {
          if (bar.time <= tStart || bar.time > tEnd) continue;
          const ratio = bar.value / pred;
          series.push({ time: bar.time, value: ratio });
          bounds.push({ time: bar.time, low: low / pred, high: high / pred });
        }
        tStart = tEnd;
      }
    }
  }
  const dedup = (arr) => {
    const seen = new Map();
    for (const p of arr) {
      if (!seen.has(p.time)) seen.set(p.time, p);
    }
    return Array.from(seen.values()).sort((a, b) => a.time - b.time);
  };
  return {
    series: dedup(series),
    bounds: dedup(bounds),
  };
}

/* =========================================================================
   Cache builder used by StockChart
   ========================================================================= */
export function buildSeriesCache(fundamentals, px, interval, lagDays, indicatorFilter = null, options = {}) {
  const income       = fundamentals.income || [];
  const cashflow     = fundamentals.cashflow || [];
  const metrics      = fundamentals.metrics || [];
  const earnings     = fundamentals.earnings || fundamentals.earningsHistory || [];
  const balanceSheet = fundamentals.balanceSheet || [];

  const zScoreYears = options.zScoreYears != null
    ? Number(options.zScoreYears)
    : (isLegacyZScore5yKey(indicatorFilter) ? 5 : DEFAULT_Z_SCORE_YEARS);
  const zFrom = (pts) =>
    rollingZScoreTimeSeries(pts || [], yearsToLookbackSec(zScoreYears), (p) => p.value)
      .filter((p) => Number.isFinite(p?.value));

  const comboMembers = isClassicNovelCombo(indicatorFilter)
    ? normalizeComboMembers(options.comboMembers)
    : null;

  // Helper to check if indicator is needed (merges legacy *_5y with base z-score ids)
  const needs = (indicator) => {
    if (!indicatorFilter) return true;
    if (comboMembers) {
      const norm = normalizeZScoreIndicatorKey(indicator);
      return comboMembers.includes(norm) || comboMembers.includes(indicator);
    }
    const norm = normalizeZScoreIndicatorKey(indicator);
    const filterNorm = normalizeZScoreIndicatorKey(indicatorFilter);
    return norm === filterNorm || indicator === indicatorFilter;
  };
  const needsAny = (keys) => keys.some(needs);

  // When indicatorFilter is set (indicators tab), skip expensive series not used by the active indicator.
  const epsCacheIndicators = ["pe_ratio", "peg_ratio", "pe_peratio", "pe_dev"];
  const epsSeries =
    !indicatorFilter || needsAny(epsCacheIndicators)
      ? buildTTM(income, "eps", lagDays, interval)
      : [];

  // Core valuation/quality series - compute only if needed
  const pbNeeded = needs("pb_ratio");
  const pbDaily  = pbNeeded ? computePBDaily(metrics, px) : [];
  const pbNormalized = pbNeeded ? zFrom(pbDaily) : [];
  const pbNormalized1y = [];
  const pbNormalized5y = [];

  const peNeeded = needs("pe_ratio");
  const peDaily  = peNeeded ? computePEDaily(income, px) : [];
  const peNormalized = peNeeded ? zFrom(peDaily) : [];
  const peNormalized1y = [];
  const peNormalized5y = [];

  const poiNeeded = needs("poi_ratio");
  const poiDaily = poiNeeded ? computePOIDaily(income, px) : [];
  const poiNormalized = poiNeeded ? zFrom(poiDaily) : [];
  const poiNormalized1y = [];
  const poiNormalized5y = [];

  const psNeeded = needs("ps_ratio");
  const psDaily  = psNeeded ? computePSDaily(income, px) : [];
  const psNormalized = psNeeded ? zFrom(psDaily) : [];
  const psNormalized1y = [];
  const psNormalized5y = [];

  const cfPriceNeeded = needs("cf_price");
  const cfDaily = cfPriceNeeded ? computePCFDaily(cashflow, income, px) : [];
  const cfPriceNormalized = cfPriceNeeded ? zFrom(cfDaily) : [];
  const cfPriceNormalized1y = [];
  const cfPriceNormalized5y = [];
  const pegRaw     = needs("peg_ratio") ? computePEGCustom(px, earnings, income, interval, lagDays, 3) : [];
  const pegQ       = needs("peg_ratio") ? computePEGQuantile(pegRaw, 5) : [];
  const evYieldNeeded = needsAny(["ev_ebitda", "ev_ebitda_z"]);
  const evYieldRaw = evYieldNeeded ? computeEVYieldSeries(metrics, interval, lagDays, px, income, balanceSheet) : [];
  const evYield = needs("ev_ebitda")
    ? (evYieldRaw || []).filter((p) => p && Number.isFinite(p.time) && Number.isFinite(p.value))
    : [];
  const evYieldZ2y = needs("ev_ebitda_z") ? zFrom(evYieldRaw) : [];
  const evYieldZ5y = [];
  const divYieldNeeded = needsAny(["div_yield", "div_yield_z", "syo"]);
  const divYield   =
    !indicatorFilter || divYieldNeeded
      ? computeDividendYield(cashflow, income, px, interval, lagDays)
      : [];
  const divYieldZ2y = needs("div_yield_z") ? zFrom(divYield) : [];
  const divYieldZ5y = [];
  const altmanZ    = needs("z_score") ? computeAltmanZ({ balanceSheet, income, px, interval }) : [];

  // Composite/oscillators - compute only if needed
  const syo        = needs("syo") ? computeSYO({ income, cashflow, px, lagDays, interval }) : [];
  const rmo        = needs("rmo") ? computeRMO(income, px, interval, lagDays) : [];
  const tamo       = needs("tamo") ? computeTAMO(balanceSheet, px, interval, lagDays) : [];
  const nimo       = needs("nimo") ? computeNIMO(income, px, interval, lagDays) : [];
  const cfao       = needs("cfao") || needs("cfao_delta") ? computeCFAO(cashflow, interval, lagDays, { normalize: true }) : [];
  const cfao_delta = needs("cfao_delta") ? computeCFAODelta(cfao) : [];
  const vmz        = needs("vmz") || needs("vmo") || needs("risk") ? computeVMZ(metrics, interval, lagDays, px, income, balanceSheet) : [];
  const cce        = needs("cce") ? computeCCE(income, cashflow, interval, lagDays) : [];
  const oey        = needs("oey") ? computeOEY(cashflow, income, px, interval, lagDays) : [];
  const vmo        = needs("vmo") || needs("risk") ? computeVMO(vmz) : [];
  const revSurprise = needs("rev_surprise") ? computeRevenueSurprise(earnings, interval, lagDays) : [];
  const ed         = needs("ed") ? computeED(earnings, interval, lagDays) : [];
  const mrvz       = needs("mrvz") ? computeMRVZFromBars(fundamentals._dailyBars, interval, lagDays) : [];
  const vddm       = needs("vddm") ? computeVDDM(fundamentals._dailyBars, { window: 252, smooth: 63, normalize: 'percentile' }) : [];
  const nupl       = needs("nupl") ? computeNUPLProxyFromBars(fundamentals._dailyBars, interval, lagDays) : [];
  const realizedPrice252 = needs("realized_price_252")
    ? computeRealizedPriceFromBars(fundamentals._dailyBars, REALIZED_PRICE_WINDOWS.realized_price_252, interval, lagDays)
    : [];
  const realizedPrice504 = needs("realized_price_504")
    ? computeRealizedPriceFromBars(fundamentals._dailyBars, REALIZED_PRICE_WINDOWS.realized_price_504, interval, lagDays)
    : [];
  const realizedPrice5y = needs("realized_price_5y")
    ? computeRealizedPriceFromBars(fundamentals._dailyBars, REALIZED_PRICE_WINDOWS.realized_price_5y, interval, lagDays)
    : [];
  const srm        = needs("srm") || needs("risk") ? computeSRM(px) : []; // Compute SRM if risk is needed
  const risk       = needs("risk") ? computeRiskComposite({ px, vmz, vmo, srm }) : [];
  const pePERatio  = needs("pe_peratio") ? computePEPERatio(earnings, px, interval, lagDays, fundamentals.analystForecasts) : [];
  const forwardPe  = needs("forward_pe") ? computeForwardPE(earnings, px, interval, lagDays, fundamentals.analystForecasts) : [];
  const ttmPe      = needs("ttm_pe") ? computeTTMPE(earnings, px, interval, lagDays) : [];
  const revenueCagr1y = needs("revenue_cagr") ? computeRevenueCAGR(income, px, interval, lagDays, 1) : [];
  const revenueCagr2y = needs("revenue_cagr") ? computeRevenueCAGR(income, px, interval, lagDays, 2) : [];
  const revenueCagrFwd = needs("revenue_cagr")
    ? computeRevenueForwardCAGR(earnings, income, px, interval, lagDays, fundamentals.analystForecasts)
    : [];
  const epsCagr1y = needs("eps_cagr") ? computeEPSCAGR(income, px, interval, lagDays, 1) : [];
  const epsCagr2y = needs("eps_cagr") ? computeEPSCAGR(income, px, interval, lagDays, 2) : [];
  const epsCagrFwd = needs("eps_cagr")
    ? computeEPSForwardCAGR(earnings, income, px, interval, lagDays, fundamentals.analystForecasts)
    : [];
  const growthAccelNeeded = needsAny(["fgpz", "gag"]);
  const gag = growthAccelNeeded && needs("gag")
    ? computeGrowthAccelerationGap(earnings, income, px, interval, lagDays, fundamentals.analystForecasts)
    : [];
  const fgpz = growthAccelNeeded && needs("fgpz")
    ? computeForwardGrowthPremiumZ(earnings, income, px, interval, lagDays, fundamentals.analystForecasts)
    : [];
  const revenueCagr = revenueCagr2y;
  const epsCagr     = epsCagr2y;
  let revVal = { series: [], bounds: [] };
  if (needs("rev_val")) {
    try {
      revVal = computeRevenueValuationIndicator({
        earnings,
        px,
        interval,
        lagDays,
        analystForecasts: fundamentals.analystForecasts,
      }) || revVal;
    } catch (err) {
      if (typeof console !== "undefined" && console.warn) {
        console.warn("[buildSeriesCache] computeRevenueValuationIndicator failed:", err);
      }
    }
  }
  let epsVal = { series: [], bounds: [] };
  if (needs("eps_val")) {
    try {
      epsVal = computeEPSValuationIndicator({
        earnings,
        px,
        interval,
        lagDays,
        analystForecasts: fundamentals.analystForecasts,
      }) || epsVal;
    } catch (err) {
      if (typeof console !== "undefined" && console.warn) {
        console.warn("[buildSeriesCache] computeEPSValuationIndicator failed:", err);
      }
    }
  }

  const cacheOut = {
    eps: epsSeries,
    altmanZ,
    pb: pbDaily,
    pbNormalized: pbNormalized,
    pbNormalized1y,
    pbNormalized5y,
    pe: peDaily,
    peNormalized: peNormalized,
    peNormalized1y,
    peNormalized5y,
    poi: poiDaily,
    poiNormalized,
    poiNormalized1y,
    poiNormalized5y,
    ps: psDaily,
    psNormalized: psNormalized,
    psNormalized1y,
    psNormalized5y,
    cfPrice: cfDaily,
    cfPriceDaily: cfDaily,
    cfPriceNormalized: cfPriceNormalized,
    cfPriceNormalized1y,
    cfPriceNormalized5y,
    peg: pegRaw,
    pegQ: pegQ,
    pegNormalized: [],
    pegNormalized1y: [],
    pegNormalized5y: [],
    evYield,
    evYieldRaw,
    evYieldZ2y,
    evYieldZ5y,
    divYield,
    divYieldZ2y,
    divYieldZ5y,
    syo,
    rmo,
    tamo,
    nimo,
    cfao,
    cfao_delta,
    vmz,
    cce,
    oey,
    vmo,
    revSurprise,
    srm,
    ed,
    mrvz,
    vddm,
    nupl,
    realizedPrice252,
    realizedPrice504,
    realizedPrice5y,
    risk,
    pePERatio,
    forwardPe,
    ttmPe,
    revenueCagr,
    revenueCagr1y,
    revenueCagr2y,
    revenueCagrFwd,
    epsCagr,
    epsCagr1y,
    epsCagr2y,
    epsCagrFwd,
    gag,
    fgpz,
    revVal,
    epsVal,
  };

  return cacheOut;
}
